import React, { useState, useContext, useEffect, useCallback, useMemo, useRef } from 'react';
import { yearsToRetirementFor, yearsToRetirementFromAge } from '../../../utils/monthlyProjection';
import { motion } from 'framer-motion';
import {
	ArrowRightLeft,
	Download,
	FileText,
	Gift,
	Info,
	Loader2,
	PiggyBank,
	Receipt,
	TrendingUp,
	Wallet
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import RescueRequestDialogV2 from '../../../components/RescueRequestDialogV2';
import RescueRentaSimulatorV2 from '../../../components/MiBeneficioV2/RescueRentaSimulatorV2';
import RescueTaxSimulatorV2 from '../../../components/SimulatorsV2/RescueTaxSimulatorV2';

import { I18nContext } from '../../../context/translations/TranslationsContext.context';
import { ArcaContext } from '../../../context/ArcaContext.context';
import useWithdrawalRequests from '../../../hooks/useWithdrawalRequests';
import useRescuesEnabled from '../../../hooks/useRescuesEnabled';
import useSeccionTraspasosEnabled from '../../../hooks/useSeccionTraspasosEnabled';
import useOneTimeContributionEnabled from '../../../hooks/useOneTimeContributionEnabled';
import useIbanGate from '../../../hooks/useIbanGate';
import useIbanGateReturn, { IBAN_GATE_RETURN_PARAM } from '../../../hooks/useIbanGateReturn';
import useTransferReturns from '../../../hooks/useTransferReturns';
import useArcaPlanReturns from '../../../hooks/useArcaPlanReturns';
import useOwnPlanReturn from '../../../hooks/useOwnPlanReturn';
import startBankDataKyc, { KYC_NOT_APPROVED } from '../../../api/kyc/startBankDataKyc';
import { reasonStopsContributions } from '../../../utils/rescueDocsCatalog';
import { getMyPensionPlanTransfers, lockTransferPlan } from '../../../api/pension-plan-transfers';
import updateMyTransfer from '../../../api/pension-plan-transfers/updateMyTransfer';
import {
	resolveActiveTransfer,
	arcaPlanFromTransfer,
	resolveActiveArcaPlan
} from '../../../utils/resolveActiveArcaPlan';
import { findArcaPlanById, findArcaPlanByName, ARCA_PLANS } from '../../../constants/arcaPlans';
import OtherPlansSheet from '../../../components/MiBeneficioV2/OtherPlansSheet';
import { PensionPlanTransfer } from '../../../api/pension-plan-transfers/interfaces/PensionPlanTransfer';
import { getKycStatus, startKyc } from '../../../api/kyc';
import startTransferSignatureKyc from '../../../api/kyc/startTransferSignatureKyc';
import { abrirEnElNavegador, irAUnaWebDeFuera } from '../../../movil/navegadorExterno';
import { esEntornoLocal, origenPublico } from '../../../movil/origenPublico';
import getSignedTransferBulletins from '../../../api/kyc/getSignedTransferBulletins';
import { KycStatusResponse } from '../../../api/kyc/interfaces/KycStatus';
import getAdhesionDetails, { AdhesionMode } from '../../../api/users/getAdhesionDetails';
import {
	isPersonalPlanType,
	resolvePersonalAdhesionMode
} from '../../../utils/resolvePersonalAdhesionMode';
import { UserPlanI } from '../../../api/users/getPlans';
import { UserAdhesionDetailsI } from '../../../api/users/interfaces/UserAdhesionDetailsI';
import { calculatePensionPlanMetrics, UserType } from '../../../utils/pensionPlanCalculations';
import getUserCostBasis, { UserCostBasisData } from '../../../api/calculators/getUserCostBasis';
import { formatValueDate, fmtEur, fmtNum } from '../../../utils/formatArcaNumbers';
import PensionPlanTransferJourneyV2 from '../../../components/PensionPlanTransferJourneyV2';
import PerformanceComparisonV2 from '../../../components/MiBeneficioV2/PerformanceComparisonV2';
import QuickActionsGridV2 from '../../../components/MiBeneficioV2/QuickActionsGridV2';
import ActivePlanSection from '../../../components/MiBeneficioV2/ActivePlanSection';
import ContributionLimitsChartV2 from '../../../components/MiBeneficioV2/ContributionLimitsChartV2';
import EducationalTipV2, { TipData } from '../../../components/MiBeneficioV2/EducationalTipV2';
import ExternalPlanCTAV2 from '../../../components/MiBeneficioV2/ExternalPlanCTAV2';
import { ExternalPlanCTA } from '../../../components/ui/external-plan-cta';
import IndividualPensionPlanFlowV2 from '../../../components/MiBeneficioV2/IndividualPensionPlanFlowV2';
import getCurrentUser from '../../../api/users/getCurrentUser';
import getUserEppConfigForMode from '../../../api/users/getUserEppConfigForMode';
import { UserEppConfigForModeI } from '../../../api/users/interfaces/UserEppConfigForMode';
import { resolveRealCompanyId } from '../../../utils/resolveRealCompanyId';
import updatePeriodicalContribution from '../../../api/users/updatePeriodicalContribution';
import getIndividualPensionPlan from '../../../api/users/getIndividualPensionPlan';
import { IndividualPensionPlan } from '../../../api/users/interfaces/IndividualPensionPlan';
import calculateMaxAportationsInfo, {
	MaxAportationsInfoFreelanceOutput
} from '../../../api/companies/calculateMaxAportationsInfo';
import getCalculatorsUserAportations, {
	PaginatedMonthContributions
} from '../../../api/calculators/getCalculatorsUserAportations';
import { GenericPaginationOutput } from '../../../api/generic-dtos/genericPaginationOutput';
import {
	classifyContribution,
	ContributionCategoryEnum
} from '../../../utils/contributionClassification';
import ContributionsEvolutionChartV2, {
	MonthlyContributionData
} from '../../../components/MiBeneficioV2/ContributionsEvolutionChartV2';
import { getTransferComparison, TransferComparisonData } from '../../../api/pension-plan-transfers';
import { detectLandingScenario, isNewPlanTransfer } from '../../../types/individualDashboard';
import { transferProjectionInputs } from '../../../utils/transferProjectionInputs';
import {
	hasSeenTransferConfirmation,
	markTransferConfirmationSeen
} from '../../../utils/transferConfirmationSeen';
import calculateNextIndividualContributionDate from '../../../utils/calculateNextIndividualContributionDate';
import { getPlanProductCodes } from '../../../utils/getPlanProductCodes';
import TarjetaAccionPendiente from '../../../components/Traspasos/TarjetaAccionPendiente';
import {
	accionPendienteDelTraspaso,
	ESTADOS_VIVOS,
	traspasoAbiertoEnInicio
} from '../../../utils/traspasos/tarjetasDeTraspaso';
import TarjetaTraspaso from '../../../components/Traspasos/TarjetaTraspaso';
import ExplorationView from '../../../components/MiBeneficioV2/ExplorationView';
import {
	isOnboardingExploration,
	resolvePrimaryTransfer
} from '../../../utils/resolvePrimaryTransfer';
import { puedeFirmarBoletin } from '../../../utils/puedeFirmarBoletin';
import {
	liveFunnelEnrollments,
	resolveFunnelPrimaryTransfer
} from '../../../utils/funnelEnrollment';
import ProcessTimeline from '../../../components/MiBeneficioV2/ProcessTimeline';
import TransferInfoCard from '../../../components/MiBeneficioV2/TransferInfoCard';
import { getOriginPlanType } from '../../../api/pension-plan-catalog/getOriginPlanType';
import { PlanType } from '../../../api/pension-plan-catalog/getArcaPlansFinancials';
import updateB2CContributionDay from '../../../api/users/updateB2CContributionDay';
import createOneTimeContribution from '../../../api/users/createOneTimeContribution';
import getOneTimeContributionLimits, {
	OneTimeContributionLimits
} from '../../../api/users/getOneTimeContributionLimits';
import { translateOneTimeContributionError } from '../../../utils/translateOneTimeContributionError';
import ChangeContributionDaySheet from '../../../components/MiBeneficioV2/ChangeContributionDaySheet';
import OneTimeContributionSheet from '../../../components/MiBeneficioV2/OneTimeContributionSheet';
import MonthlyContributionSheet from '../../../components/MiBeneficioV2/MonthlyContributionSheet';
import { analytics } from '../../../services/analytics';
import { shouldFireContributionUpdated } from '../../../utils/shouldFireContributionUpdated';
import { isDemoAccountBlocked } from '../../../utils/demoAccountInterceptor';
import {
	CONTRIBUTION_ADJUSTMENT_METHODS,
	ContributionAdjustmentMethod
} from '../../../constants/contributionAdjustmentMethods';
import IbanRequiredSheet from '../../../components/MiBeneficioV2/IbanRequiredSheet';
import { useToast } from '../../../components/ui/use-toast';
// El único Toaster montado en App.tsx es el de sonner: los avisos que salen por
// `useToast` (Radix) no tienen renderizador y se pierden. La aportación puntual
// usa sonner para que el usuario vea de verdad el resultado.
import { toast as sonnerToast } from '../../../components/ui/sonner';
import { originPlanCode } from '../../../utils/originPlanCode';

const CHART_MONTH_LABELS = [
	'Ene',
	'Feb',
	'Mar',
	'Abr',
	'May',
	'Jun',
	'Jul',
	'Ago',
	'Sep',
	'Oct',
	'Nov',
	'Dic'
];

interface IndividualDashboardV2Props {
	onOpenInviteModal?: () => void;
	/**
	 * Plan personal que el usuario ha elegido en el selector de vista. Manda en
	 * TODO lo de esta pantalla —saldos, config, importe, día—: con dos planes del
	 * mismo tipo, `mode` no nombra una fila y se acabaría pintando un plan y
	 * escribiendo en otro. `null` mientras la lista no ha llegado, o con un solo
	 * plan: ahí el backend resuelve solo y el comportamiento es el de siempre.
	 */
	selectedPersonalPlanId?: string | null;
	/**
	 * Nombre comercial del plan elegido, para el título.
	 *
	 * 🔴 Antes se deducía del TRASPASO (`arcaPlanFromTransfer`) y, sin traspaso,
	 * caía a un `'Horos Internacional, PP'` literal. Con un solo plan colaba; con
	 * dos, la pantalla decía un plan y enseñaba otro — visto en DEV el 25-ago.
	 * El nombre lo sabe `GET /users/plans`, que ya carga el Dashboard.
	 */
	selectedPlanDisplayName?: string | null;
	/**
	 * Slug del catálogo del plan elegido (`GET /users/plans` → `planCatalogSlug`), para su
	 * rentabilidad. Como el nombre: el traspaso puede apuntar a otro plan (un PPI que en el
	 * traspaso figura como Indexa Acciones), y un plan que ya no se ofrece ni siquiera está en
	 * `ARCA_PLANS` (chirigota#598). Sin él, el del traspaso, como antes.
	 */
	selectedPlanCatalogSlug?: string | null;
	/**
	 * `GET /users/plans` aún no ha llegado: no se sabe su plan real. Mientras, la proyección está
	 * «cargando»; si no, se vería un instante la del traspaso (otro plan) o «sin datos».
	 */
	plansLoading?: boolean;
	/**
	 * Tipo del plan elegido (`user_epp_config.type`, vía `GET /users/plans`).
	 *
	 * 🔴 Es quien decide el `mode` de las lecturas, POR ENCIMA de los flags del
	 * usuario. El flag describe a la PERSONA (`is_freelance` lo pone el funnel);
	 * el tipo describe el PLAN que se está pidiendo, que es lo que el backend
	 * necesita saber. Cuando no coinciden gana el plan, porque `mode` y
	 * `userEppConfigId` viajan juntos en la misma llamada y el backend los cruza
	 * (`findOne({ type, id })`): si no casan, no hay plan que devolver.
	 *
	 * Incidente PROD 02-sep-2026: una persona con `is_freelance = true` y dos
	 * planes personales (un INDIVIDUAL y uno de AUTÓNOMOS) recibía 400
	 * `USER_HAS_NO_PLAN_FOR_FREELANCE_MODE` y se quedaba sin saldo en pantalla,
	 * porque el Dashboard preselecciona el primer plan no archivado (el
	 * INDIVIDUAL) y aquí se mandaba FREELANCE.
	 *
	 * `null`/`undefined` (lista aún sin cargar, o plan sin catálogo resuelto) →
	 * se mantiene el criterio anterior por flags, que es el correcto para quien
	 * tiene un solo plan.
	 */
	selectedPlanType?: UserPlanI['type'];
	/** Edad del usuario (de Dashboard). Para el horizonte de proyección (67 − edad). */
	currentAge?: number;
	/** Navega a la pestaña de Aportaciones (CTA "Ver detalle" de la gráfica de evolución). */
	onNavigateToContributions?: () => void;
	/**
	 * Navega a la pestaña de Simuladores (CTA de los tips educativos). Solo se pasa para
	 * autónomos (los individuales no tienen esa pestaña → el CTA hace scroll al slider).
	 */
	onNavigateToSimulators?: (tab: 'fiscal' | 'planificador') => void;
	/**
	 * Plan individual EN PROCESO: empleado B2B con su alta del funnel viva y sin plan
	 * personal todavía. Tiene DOS peticiones del funnel —el alta y el traspaso— y la
	 * vista se ancla al traspaso por `kind`, no por el orden en que llegan; al confirmar
	 * el plan se fija y se bloquea en las dos, para que la ejecución del alta encuentre
	 * su petición por plan.
	 */
	pendingPersonalPlan?: boolean;
	/**
	 * Tras confirmar o cambiar el plan en proceso: el Dashboard vuelve a pedir las
	 * peticiones para que el selector y la tarjeta de Mi beneficio cambien sin recargar.
	 */
	onFunnelRequestsChanged?: () => void;
	/**
	 * Euros que suma el enlace del correo (Arca-Team/product#3). Lo entrega el Dashboard: con
	 * él se abre `MonthlyContributionSheet` en `current + amount` para el plan elegido.
	 */
	contributionPresetDelta?: number | null;
	/** El modal ya se abrió con el preset: el Dashboard lo olvida para no reabrirlo. */
	onContributionPresetConsumed?: () => void;
}

const IndividualDashboardV2: React.FC<IndividualDashboardV2Props> = ({
	onOpenInviteModal,
	pendingPersonalPlan = false,
	onFunnelRequestsChanged,
	selectedPersonalPlanId,
	selectedPlanDisplayName,
	selectedPlanCatalogSlug,
	plansLoading = false,
	selectedPlanType,
	currentAge: knownAge,
	onNavigateToContributions,
	onNavigateToSimulators,
	contributionPresetDelta = null,
	onContributionPresetConsumed
}) => {
	const {
		state: { translate, langCode }
	} = useContext(I18nContext);
	const { token, companyId, userId } = useContext(ArcaContext);
	const { toast } = useToast();
	// Sin su edad, 30 años solo para el horizonte: nunca para pedir tramos de ciclo de vida.
	const currentAge = knownAge ?? 30;

	const activePlanRef = React.useRef<HTMLDivElement>(null);
	const [debugActivePlan, setDebugActivePlan] = React.useState(false);
	const [userTransfers, setUserTransfers] = useState<PensionPlanTransfer[]>([]);
	const [isLoadingTransfers, setIsLoadingTransfers] = useState(true);
	const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
	const [adhesionDetails, setAdhesionDetails] = useState<UserAdhesionDetailsI[]>([]);
	// Base de coste de traspasos (back). Fuente fiable (`pension_plan_contributions`):
	// el cálculo del front por `pension_plan_transfers` filtra por typology y deja "--"
	// a quien traspasó desde un antiguo plan de EMPLEO. En B2C aplicamos directo (sin gate):
	// el plan individual ES lo que mostramos.
	const [costBasis, setCostBasis] = useState<UserCostBasisData | null>(null);
	const [loadingAdhesionDetails, setLoadingAdhesionDetails] = useState(false);
	const [kycStatusData, setKycStatusData] = useState<KycStatusResponse | null>(null);
	/** Cuándo se preguntó por última vez, para no duplicar consulta al volver a la pestaña. */
	const ultimaConsultaKyc = useRef(0);
	/** Cuál es la última consulta pedida: las respuestas de las anteriores se descartan. */
	const consultaKycEnCurso = useRef(0);
	/**
	 * Peticiones cuyo boletín YA consta firmado. Comparsa no se entera de las
	 * firmas (el documento vive en el KYC), así que hay que preguntárselo: sin
	 * esto, el paso de firmar no desaparecería nunca.
	 *
	 * Vacío significa «no consta», no «sin firmar»: si la consulta falla se
	 * enseña el botón igualmente. Molesta a quien ya firmó, pero esconderlo a
	 * quien no ha firmado dejaría su traspaso sin el papel.
	 */
	const [signedBulletinIds, setSignedBulletinIds] = useState<string[]>([]);
	// Si ya sabemos qué boletines están firmados. Hasta entonces la lista vacía no dice «sin firmar».
	const [firmadosResueltos, setFirmadosResueltos] = useState(false);
	/*
	 * 🔴 Elegir el plan de destino desde la tarjeta de arriba, con `OtherPlansSheet`.
	 *
	 * Hace falta porque un traspaso pedido DESDE la plataforma (`origin: PLATFORM`) no entra en
	 * modo exploración —`isOnboardingExploration` lo excluye a propósito—, así que quien cerrara
	 * el modal antes de elegir plan se quedaba con un traspaso creado y sin ninguna vía para
	 * terminarlo.
	 *
	 * ⚠️ El primer intento fue forzar esa pantalla de comparación, y estaba mal por tres motivos
	 * que encontró la revisión: no tenía forma de volver (el estado no se apagaba nunca), saluda
	 * como a un recién llegado y anuncia una verificación de identidad que esta persona YA pasó,
	 * y al confirmar no persistía el destino —relanzaba un KYC aprobado y bloqueaba el traspaso
	 * sin plan—. Esta hoja hace justo una cosa y la hace bien: `updateMyTransfer`, y se cierra.
	 */
	const [eligiendoDestino, setEligiendoDestino] = useState(false);
	const [signingTransferId, setSigningTransferId] = useState<string | null>(null);
	const [userFirstName, setUserFirstName] = useState('');
	const [userEmail, setUserEmail] = useState('');
	// `users.bank_account`, del mismo `current-user` que ya se pide: sin cuenta
	// no se puede domiciliar, y hay que pararlo ANTES de la pantalla de importe.
	const [userBankAccount, setUserBankAccount] = useState<string>('');
	// Si `current-user` ya respondió. `userBankAccount` vacío no distingue «aún
	// no lo sé» de «no tiene cuenta», y confundirlos bloquearía a quien SÍ la
	// tiene cuando la petición falla.
	const [userBankAccountResolved, setUserBankAccountResolved] = useState(false);
	const [showRescueDialog, setShowRescueDialog] = useState(false);
	const [showCalculatorModal, setShowCalculatorModal] = useState(false);
	const [monthlyContribution, setMonthlyContribution] = useState<number>(0);
	/**
	 * La mensual GUARDADA del plan. No es `monthlyContribution`: esa la mueve también el
	 * importe retomado del mini-KYC, que aún no se ha guardado. Es lo «previo» del evento
	 * `contribution_updated` y el suelo del modal del enlace del correo.
	 */
	const [savedMonthlyContribution, setSavedMonthlyContribution] = useState<number>(0);
	/** Euros del enlace del correo mientras su modal está abierto; `null` cerrado. */
	const [monthlyPresetDelta, setMonthlyPresetDelta] = useState<number | null>(null);
	/**
	 * Hay un importe retomado del mini-KYC y manda sobre el configurado.
	 *
	 * El usuario acaba de elegirlo; lo que hay en la config es lo que tenía
	 * ANTES del desvío. Sin esto, la respuesta de `getUserEppConfigForMode`
	 * —que llega por red, siempre después— pisaba el importe recuperado y el
	 * slider le devolvía el viejo: exactamente lo que el retomar viene a evitar.
	 */
	const resumedAmountWins = React.useRef(false);
	const [nextContributionDate, setNextContributionDate] = useState<string | undefined>(undefined);
	const [contributionDay, setContributionDay] = useState<number | undefined>(undefined);
	const [comparisonData, setComparisonData] = useState<TransferComparisonData | null>(null);
	// true cuando la petición de comparación ha terminado (con datos, sin datos o con
	// error). Gatea el montaje de ExplorationView para no pintar primero su versión
	// parcial (solo "Tu elección") y saltar luego a la completa cuando llegan los datos.
	const [comparisonLoaded, setComparisonLoaded] = useState(false);
	const [originPlanType, setOriginPlanType] = useState<PlanType | null>(null);
	const [isChangeDaySheetOpen, setIsChangeDaySheetOpen] = useState(false);
	const [isOneTimeSheetOpen, setIsOneTimeSheetOpen] = useState(false);
	const [oneTimeLimits, setOneTimeLimits] = useState<OneTimeContributionLimits | null>(null);
	const [loadingOneTimeLimits, setLoadingOneTimeLimits] = useState(false);
	// Ya ha aportado en ESTA sesión. Es el suelo del guard de "una al día" para
	// cuando `/limits` no está disponible: lo sabemos porque lo hicimos nosotros.
	const [contributedTodayInSession, setContributedTodayInSession] = useState(false);
	// Identifica la apertura del modal más reciente, para descartar respuestas de
	// `/limits` que lleguen tarde y pisen a la buena.
	const oneTimeLimitsRequestRef = React.useRef(0);

	const [isFreelance, setIsFreelance] = useState<boolean | undefined>(undefined);
	// Tope (€/mes) del slider de aportación para autónomos: su máximo legal real,
	// calculado por el backend (no se re-deriva en el front). `undefined` → el
	// slider usa su default (límite del plan individual).
	const [freelanceMaxContribution, setFreelanceMaxContribution] = useState<number | undefined>(
		undefined
	);
	const [chartContributions, setChartContributions] =
		useState<GenericPaginationOutput<PaginatedMonthContributions> | null>(null);
	const [chartYear, setChartYear] = useState(new Date().getFullYear());
	const [regulationLink, setRegulationLink] = useState<string>('');
	// Plan de pensiones individual EXTERNO declarado por el usuario (otra gestora).
	// Alimenta el tramo "Plan individual" de la tarjeta de límites y el CTA de registro.
	const [individualPlan, setIndividualPlan] = useState<IndividualPensionPlan | null>(null);
	const [showExternalPlanFlow, setShowExternalPlanFlow] = useState(false);
	// Modo derivado de los FLAGS del usuario. Es el fallback: manda solo mientras
	// no se sabe de qué tipo es el plan que se está mirando (ver `adhesionMode`).
	// `null` = aún no sabemos el tipo de user → el fetch espera (evita la race
	// con getCurrentUser).
	const [userDerivedAdhesionMode, setUserDerivedAdhesionMode] = useState<AdhesionMode | null>(null);

	/**
	 * El `mode` de TODAS las lecturas de esta pantalla.
	 *
	 * Manda el TIPO del plan elegido; los flags del usuario son el fallback. La
	 * regla y su porqué (incidente del 02-sep-2026) viven en
	 * `resolvePersonalAdhesionMode`, compartida con Movimientos.
	 */
	const adhesionMode = useMemo<AdhesionMode | null>(
		() => resolvePersonalAdhesionMode(selectedPlanType, userDerivedAdhesionMode),
		[selectedPlanType, userDerivedAdhesionMode]
	);
	// Config de `user_epp_config` del plan de ESTE modo. Guardarla entera (y no
	// solo los campos sueltos que se pintan) es lo que permite saber que el plan
	// existe de verdad y a qué plan de catálogo pertenece.
	const [personalPlanConfig, setPersonalPlanConfig] = useState<UserEppConfigForModeI | null>(null);

	// `resolveRealCompanyId` y no `companyId ?? null`: en localStorage un usuario
	// sin empresa queda como la CADENA "null", que es truthy. Mandarla al backend
	// hace que el guard `hasCompanyAccess(userId, "null")` de
	// `POST /feature-flags/check` responda 401, el hook lo capture y la flag quede
	// apagada EN SILENCIO — justo para el B2C puro al que se le está abriendo.
	const { enabled: oneTimeContributionEnabled } = useOneTimeContributionEnabled(
		token,
		resolveRealCompanyId(companyId),
		userId
	);

	// ¿Se puede aportar puntualmente al plan que se está viendo?
	//
	// Lo decide el PLAN, no la persona: la aportación puntual es una operación
	// sobre un plan PERSONAL, y quien lo tiene lo opera — tenga empresa o no. Un
	// empleado con plan individual propio aporta al SUYO (caso Gonzalo/Horos).
	//
	// `personalPlanConfig` es que `getUserEppConfigForMode` haya devuelto config
	// para este modo. No comprobamos `adhesionMode` aparte: en esta vista solo se
	// fija a INDIVIDUAL o FREELANCE, así que sería una tautología —y ya se quitó
	// una vez por serlo (8110ab5d)—; mientras vale `null`, el fetch ni se lanza y
	// la config sigue siendo `null`.
	//
	// ⚠️ Esto NO es la misma regla que el backend, y conviene no creérselo: el
	// backend (`resolvePersonalPlanConfig`) exige `status = active` y usa su propio
	// `isPersonalPlanConfig`, mientras que `getUserEppConfigForMode` no filtra por
	// status y tiene un fail-open cuando `type` está sin resolver. Es un filtro
	// PREVIO para no ofrecer el botón a quien se sabe que va a ser rechazado, no
	// una garantía: la palabra final la tiene siempre el backend.
	const canMakeOneTimeContribution = oneTimeContributionEnabled && !!personalPlanConfig;

	// Gate de IBAN: sin cuenta bancaria no se puede cobrar, así que se para
	// ANTES de la pantalla de importe en los dos CTA. Se ancla al PLAN —hay plan
	// personal resuelto y falta `bankAccount`—, nunca al tipo de usuario: un plan
	// de empleo no necesita IBAN aunque sea la misma persona.
	// Pantalla que explica por qué se pide la cuenta y lleva al mini-KYC.
	const [ibanSheetOpen, setIbanSheetOpen] = useState(false);
	const [ibanSheetLoading, setIbanSheetLoading] = useState(false);
	const [ibanSheetError, setIbanSheetError] = useState<string | null>(null);
	/**
	 * Cerrar durante el sync de vuelta tiene que funcionar. `open` también lo levanta
	 * `ibanKycSyncing`, que viene del hook y no se puede apagar desde aquí, así que una X que solo
	 * bajaba `ibanSheetOpen` no hacía nada — y con el CTA deshabilitado por `loading`, el diálogo se
	 * quedaba sin un solo control que respondiera. Si además `syncOwnProfile` no resolviera nunca
	 * (el hook no tiene timeout), la persona quedaba encerrada.
	 *
	 * Lo que se cierra es la ventana; el sync sigue en segundo plano. Se rearma al abrir la hoja por
	 * cualquier motivo nuevo, incluido que el sync acabe fallando: eso sí hay que contárselo.
	 */
	const [ibanSyncDismissed, setIbanSyncDismissed] = useState(false);

	const {
		blocked: ibanGateBlocked,
		loading: ibanGateLoading,
		notifyBlocked: notifyIbanGateBlocked
	} = useIbanGate({
		token,
		companyId: resolveRealCompanyId(companyId),
		userId,
		bankAccount: userBankAccount,
		bankAccountResolved: userBankAccountResolved,
		hasPersonalPlan: !!personalPlanConfig
	});

	// Conjunto de CPSIs de ESTE plan (multi-cuenta) para scopear aportaciones/base de
	// coste por plan. Vacío hasta que llegan las adhesiones → el backend lee legacy.
	const planProductCodes = useMemo(() => getPlanProductCodes(adhesionDetails), [adhesionDetails]);

	const loadUserTransfers = useCallback(async () => {
		if (!token) return;
		setIsLoadingTransfers(true);
		try {
			const response = await getMyPensionPlanTransfers(token);
			if (response.success && response.data) {
				setUserTransfers(response.data);
			}
		} catch (error) {
			console.error('Error loading user transfers:', error);
		} finally {
			setIsLoadingTransfers(false);
		}
	}, [token]);

	/**
	 * Qué boletines constan firmados. Se pide junto a los traspasos porque es la
	 * misma pantalla y el dato solo sirve acompañado de ellos.
	 *
	 * Si falla, la lista se queda vacía a propósito: el cliente verá el paso de
	 * firmar aunque ya lo hubiera hecho, y firmar otra vez sustituye el documento
	 * en su sitio. Al revés le dejaríamos el traspaso sin papel.
	 */
	const loadSignedBulletins = useCallback(async () => {
		if (!token) return;
		try {
			const response = await getSignedTransferBulletins(token);
			if (response.success && response.data) {
				setSignedBulletinIds(response.data.signedTransferIds ?? []);
			}
		} catch (error) {
			console.error('Error loading signed bulletins:', error);
		} finally {
			setFirmadosResueltos(true);
		}
	}, [token]);

	/**
	 * Llevar al cliente a firmar el boletín de ESTE traspaso.
	 *
	 * El KYC va acotado a firmar: no repite la verificación de identidad, que
	 * esta persona ya pasó al traerse su primer plan.
	 */
	const handleSignBulletin = useCallback(
		async (transferId: string) => {
			if (!token || signingTransferId) return;
			setSigningTransferId(transferId);
			try {
				const returnUrl = `${origenPublico()}${window.location.pathname}`;
				const res = await startTransferSignatureKyc(token, { transferId, returnUrl });
				const url = res.data?.url;
				if (!url) throw new Error('sin url');
				irAUnaWebDeFuera(url, () => setSigningTransferId(null));
			} catch (error) {
				console.error('Error starting bulletin signature:', error);
				setSigningTransferId(null);
				toast({
					variant: 'destructive',
					title: translate('individual.process.signError.title'),
					description: translate('individual.process.signError.description')
				});
			}
		},
		[token, signingTransferId, toast, translate]
	);

	/**
	 * `isCancelled` permite descartar una respuesta que ya no interesa.
	 *
	 * 🔴 Desde que el modo lo decide el PLAN elegido, dos lecturas pueden estar en
	 * vuelo a la vez: el usuario cambia de plan en el selector, o la lista de
	 * planes llega después de `getCurrentUser`. Sin esto se aplican por orden de
	 * LLEGADA, y la respuesta del plan que ya nadie mira pinta su saldo bajo el
	 * nombre y los CPSI del otro — un saldo equivocado con HTTP 200, que es peor
	 * que el 400 que este cambio viene a quitar. Mismo patrón que el efecto de
	 * `getUserEppConfigForMode`, cuyo comentario ya lo anticipaba.
	 */
	const fetchAdhesionDetails = useCallback(
		async (isCancelled: () => boolean) => {
			// Espera a conocer el tipo de user (adhesionMode) antes de pedir: con el
			// mode equivocado el backend manda un FONT inválido a Caser y la respuesta
			// vuelve vacía (caso autónomos societarios, ej. admins Alhambra).
			if (!token || !adhesionMode) return;
			setLoadingAdhesionDetails(true);
			try {
				// Vista Personal: pedimos explícitamente el plan personal para que
				// usuarios mixtos (B2B con rol 'individual') reciban los saldos de SU
				// plan (Horos / Indexa / PPES autónomos) en lugar del plan empleo de su
				// empresa. Ver Fase 3 (comparsa PR #590).
				const adhesionRes = await getAdhesionDetails(
					token,
					adhesionMode,
					selectedPersonalPlanId ?? undefined
				);
				if (isCancelled()) return;
				if (Array.isArray(adhesionRes.data)) {
					setAdhesionDetails(adhesionRes.data);
				}
			} catch (error) {
				console.error('Error fetching adhesion details:', error);
			} finally {
				// También el spinner: apagarlo desde una respuesta caducada dejaría la
				// pantalla dando por buena una carga que sigue en curso.
				if (!isCancelled()) setLoadingAdhesionDetails(false);
			}
			// `selectedPersonalPlanId` en dependencias: cambiar de entrada en el selector
			// tiene que RE-pedir los datos, o la pantalla se queda con los del plan
			// anterior y enseña un saldo que no es el de ese plan.
		},
		[token, adhesionMode, selectedPersonalPlanId]
	);

	useEffect(() => {
		loadUserTransfers();
		loadSignedBulletins();
	}, [loadUserTransfers, loadSignedBulletins]);

	useEffect(() => {
		if (!token) return;
		getUserCostBasis(token, planProductCodes)
			.then(setCostBasis)
			.catch((error) => console.error('Error fetching cost basis:', error));
	}, [token, planProductCodes]);

	/**
	 * Recarga los datos del usuario. Extraído del efecto para poder volver a
	 * llamarlo al volver del mini-KYC: hasta que no se relee, `bankAccount`
	 * sigue vacío en memoria y el gate seguiría bloqueando.
	 */
	const refreshCurrentUser = useCallback(() => {
		if (!token) return;
		getCurrentUser(token)
			.then((response) => {
				if (response?.data?.user?.name) {
					const fullName = response.data.user.name;
					const first = fullName.split(' ')[0];
					setUserFirstName(first.charAt(0).toUpperCase() + first.slice(1).toLowerCase());
				}
				setUserEmail(response?.data?.user?.email || '');
				setUserBankAccount(response?.data?.user?.bankAccount || '');
				setUserBankAccountResolved(true);
				const freelance = response?.data?.user?.isFreelance;
				setIsFreelance(!!freelance);
				setUserDerivedAdhesionMode(freelance ? 'FREELANCE' : 'INDIVIDUAL');
			})
			.catch(() => {
				// Sin datos del user no podemos derivar el tipo: degradamos al
				// comportamiento previo (INDIVIDUAL) para no dejar la vista sin saldo.
				// Fijamos isFreelance=false (en vez de dejarlo undefined) para no
				// bloquear el render del bloque de plan activo (ver guard del flicker).
				setUserDerivedAdhesionMode('INDIVIDUAL');
				setIsFreelance(false);
			});
	}, [token]);

	useEffect(() => {
		refreshCurrentUser();
	}, [refreshCurrentUser]);

	// Config de aportación (monto/día/próxima fecha/regulación) del plan de ESTE modo.
	// Reemplaza la lectura de `current-user.userEppConfig`: en multi-cuenta esa fila
	// pertenece siempre al plan de EMPLEO, nunca al plan individual/autónomo de este
	// dashboard. Con 1 sola fila (99% del parque), el backend la devuelve igual sin
	// importar el modo pedido — mismo comportamiento que antes (Fase 4, épica multi-cuenta).
	useEffect(() => {
		if (!token || !adhesionMode) return;
		// Bandera de cancelación: sin ella, dos respuestas en vuelo se aplican por
		// orden de LLEGADA y no de emisión. Aquí no es cosmético — esta config
		// decide a qué plan va el dinero (`oneTimePlanCatalogId`), así que una
		// respuesta vieja pisando a la nueva sería imputar la aportación al plan
		// equivocado en cuanto `adhesionMode` sea conmutable. Mismo patrón que
		// `useContactDataBanner`.
		let cancelled = false;

		// El plan ELEGIDO manda: con dos del mismo tipo, `mode` no nombra una fila.
		getUserEppConfigForMode(token, adhesionMode, selectedPersonalPlanId ?? undefined)
			.then((response) => {
				if (cancelled) return;
				const cfg = response?.data;
				// Que exista config para este modo es la señal de que el plan es
				// suyo, y es lo que abre la aportación puntual.
				setPersonalPlanConfig(cfg ?? null);
				if (!cfg) return;
				const configuredContribution = cfg.data?.pensionContribution;
				setSavedMonthlyContribution(
					typeof configuredContribution === 'number' ? configuredContribution : 0
				);
				if (
					typeof configuredContribution === 'number' &&
					configuredContribution > 0 &&
					!resumedAmountWins.current
				) {
					setMonthlyContribution(configuredContribution);
				}
				// El día/periodicidad de la aportación periódica vive en campos distintos
				// según el tipo de plan: autónomos en `freelance*`, individuales en
				// `individual*`.
				const isFreelanceMode = adhesionMode === 'FREELANCE';
				const contributionDay = isFreelanceMode
					? cfg.data?.freelanceContributionDay
					: cfg.data?.individualContributionDay;
				if (typeof contributionDay === 'number' && contributionDay > 0) {
					setContributionDay(contributionDay);
				}
				const contributionPeriodicity = isFreelanceMode
					? cfg.data?.freelanceContributionPeriodicity
					: cfg.data?.individualContributionPeriodicity;
				const nextDate = calculateNextIndividualContributionDate({
					today: new Date(),
					day: contributionDay,
					periodicity: contributionPeriodicity
				});
				if (nextDate) {
					const locale = langCode === 'en' ? 'en-US' : 'es-ES';
					setNextContributionDate(
						nextDate.toLocaleDateString(locale, {
							day: 'numeric',
							month: 'long',
							year: 'numeric',
							timeZone: 'UTC'
						})
					);
				}
				if (cfg.linkRegulation) setRegulationLink(cfg.linkRegulation);
			})
			.catch((error) => {
				if (cancelled) return;
				// Sin saber si el plan es suyo no se ofrece la aportación puntual:
				// ante la duda, no enseñar un botón que va a acabar en un rechazo.
				setPersonalPlanConfig(null);
				console.error('Error fetching user epp config:', error);
			});

		return () => {
			cancelled = true;
		};
	}, [token, adhesionMode, langCode, selectedPersonalPlanId]);

	/**
	 * Cómo acabó: `saved`, `iban` (se paró y se abrió la pantalla del IBAN), `skipped` (no se
	 * pudo intentar todavía) o `error`. El slider lo ignora; el modal del enlace del correo lo
	 * usa para no cerrarse sin haber guardado.
	 */
	const handleConfigureContribution = useCallback(
		async (
			amount: number,
			adjustmentMethod?: ContributionAdjustmentMethod
		): Promise<'saved' | 'iban' | 'skipped' | 'error'> => {
			if (!token || !adhesionMode) return 'skipped';

			// Bajar a 0 € es dejar de aportar: no se cobra nada, así que el IBAN no
			// hace falta y el gate no aplica (product#24). Tampoco se espera a la
			// flag: no hay ningún cargo que evitar.
			const needsIban = amount > 0;

			// Mientras el gate no sabe (la flag en vuelo) no se escribe: `blocked`
			// vale `false` en esa ventana, y dejar pasar ahí es justo lo que el
			// gate viene a evitar. Si la comprobación falla, `loading` vuelve a
			// `false` y se sigue como siempre — no deja el CTA muerto.
			if (needsIban && ibanGateLoading) return 'skipped';

			// Sin IBAN no se configura nada: quedaría una domiciliación esperando
			// un cargo que no puede salir. El importe elegido se guarda para
			// retomarlo cuando vuelva del mini-KYC.
			if (needsIban && ibanGateBlocked) {
				notifyIbanGateBlocked('periodic', amount);
				// Sin limpiar, la hoja reabría con el error de un intento anterior ya puesto, antes
				// de que la persona hubiera hecho nada.
				setIbanSheetError(null);
				setIbanSyncDismissed(false);
				setIbanSheetOpen(true);
				return 'iban';
			}

			try {
				await updatePeriodicalContribution(
					token,
					amount,
					false,
					adhesionMode,
					// El plan por su FILA: `mode` no nombra una cuando el usuario
					// tiene dos del mismo tipo, y el backend responde
					// `PLAN_REQUIRED` en vez de elegir por él. Es el mismo plan
					// cuyos datos está mostrando esta vista.
					personalPlanConfig?.id
				);
				setMonthlyContribution(amount);
				setSavedMonthlyContribution(amount);
				// Mismo evento y contrato que el modal B2B (Arca-Team/product#3, §3.3).
				if (shouldFireContributionUpdated(savedMonthlyContribution, amount, adjustmentMethod)) {
					analytics.contributionUpdated({
						previousAmount: savedMonthlyContribution,
						newAmount: amount,
						adjustmentMethod,
						mode: adhesionMode
					});
				}
				return 'saved';
			} catch (error) {
				console.error('Error updating periodical contribution:', error);
				return 'error';
			}
		},
		// `personalPlanConfig` va en las dependencias: sin él el handler se queda
		// con el `null` del primer render y mandaría la escritura SIN plan —
		// justo el caso que el backend rechaza con `PLAN_REQUIRED`.
		[
			token,
			adhesionMode,
			personalPlanConfig,
			ibanGateBlocked,
			ibanGateLoading,
			notifyIbanGateBlocked,
			savedMonthlyContribution
		]
	);

	/**
	 * El enlace del correo abre el modal mensual en cuanto se sabe la config del plan ELEGIDO
	 * (su id es el de la fila): con la de otro plan, el suelo y la escritura serían de otro.
	 */
	useEffect(() => {
		if (!contributionPresetDelta || !personalPlanConfig) return;
		if (selectedPersonalPlanId && personalPlanConfig.id !== selectedPersonalPlanId) return;
		setMonthlyPresetDelta(contributionPresetDelta);
		// Este modal solo lo abre el correo: mismo evento que el B2B, para medir el embudo
		// (chirigota#630).
		analytics.contributionModalOpened({ sourcePage: window.location.pathname, fromEmail: true });
		onContributionPresetConsumed?.();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contributionPresetDelta, personalPlanConfig, selectedPersonalPlanId]);

	// Con tope si lo hay (el mismo que el slider; el del autónomo puede llegar después), pero
	// nunca por debajo de lo que ya aporta: el modal no deja bajar de ahí.
	const presetRaisedAmount =
		monthlyPresetDelta === null ? 0 : savedMonthlyContribution + monthlyPresetDelta;
	const presetInitialAmount =
		freelanceMaxContribution === undefined
			? presetRaisedAmount
			: Math.max(savedMonthlyContribution, Math.min(presetRaisedAmount, freelanceMaxContribution));

	const handleConfirmMonthlyPreset = async (amount: number) => {
		const adjustmentMethod =
			amount === presetInitialAmount
				? CONTRIBUTION_ADJUSTMENT_METHODS.PRESET_DELTA
				: CONTRIBUTION_ADJUSTMENT_METHODS.MANUAL_SLIDER;
		const result = await handleConfigureContribution(amount, adjustmentMethod);
		// Se cierra si se guardó o si el gate abrió la pantalla del IBAN en su lugar. Si no se
		// pudo, sigue abierto para reintentar; con un error, además, se avisa.
		if (result === 'saved' || result === 'iban') {
			setMonthlyPresetDelta(null);
			return;
		}
		if (result === 'error') {
			sonnerToast.error(translate('benefit.simulator.saveErrorTitle'), {
				description: translate('benefit.simulator.saveErrorDescription')
			});
		}
	};

	/**
	 * Plan al que va la aportación. Es `null` en configs antiguas pendientes del
	 * backfill; en ese caso no viaja y el backend resuelve el único plan personal
	 * del usuario.
	 */
	/**
	 * Arranca el mini-KYC de datos bancarios: pide la URL a comparsa y saca al
	 * usuario de la aplicación.
	 *
	 * La `returnUrl` lleva el marcador que nos dirá, al volver, que hay que
	 * sincronizar su perfil antes de dejarle seguir.
	 */
	/**
	 * `translate` devuelve LA PROPIA CLAVE cuando no la encuentra, así que un
	 * `||` no basta: el mensaje por defecto no se usaría nunca y el usuario
	 * leería `individual.ibanGate.errorNoPlan`. Visto en local el 09/09/26.
	 */
	const ibanGateText = useCallback(
		(key: string, fallback: string) => {
			const value = translate(key);
			return !value || value === key ? fallback : value;
		},
		[translate]
	);

	const handleStartIbanKyc = useCallback(async () => {
		if (!token) return;

		// Por el traspaso activo y, si de ahí no sale, por el NOMBRE del plan que
		// la vista está enseñando: hay usuarios cuyos traspasos no llevan
		// `selectedArcaPlanId` (visto en dev con multi-plan), y sin plan comparsa
		// rechaza la petición.
		const arcaPlanId =
			resolveActiveArcaPlan(userTransfers)?.id ??
			(selectedPlanDisplayName ? findArcaPlanByName(selectedPlanDisplayName)?.id : undefined);
		if (!arcaPlanId) {
			// Sin plan resuelto, comparsa rechazaría la petición: mejor decirlo
			// que mandar al usuario a un enlace que no existe.
			setIbanSheetError(
				ibanGateText('individual.ibanGate.errorNoPlan', 'No hemos podido identificar tu plan.')
			);
			return;
		}

		setIbanSheetLoading(true);
		setIbanSheetError(null);
		try {
			const returnUrl = `${origenPublico()}${window.location.pathname}?${IBAN_GATE_RETURN_PARAM}=1`;
			const res = await startBankDataKyc(token, {
				operationType: 'aportacion_periodica',
				returnUrl,
				arcaPlanId
			});

			// Reintentar no lo arregla: el genérico («Inténtalo de nuevo») no sirve aquí.
			if (res.success === false && res.message === KYC_NOT_APPROVED) {
				setIbanSheetError(
					ibanGateText(
						'individual.ibanGate.errorKycNotApproved',
						'Para añadir tu cuenta necesitamos haber verificado tu identidad. Estamos revisándola; te avisaremos en cuanto esté lista.'
					)
				);
				setIbanSheetLoading(false);
				return;
			}

			// Cuenta demo: ya sale su aviso único (DemoAccountBlockedNotice), sin error debajo.
			if (isDemoAccountBlocked(res)) {
				setIbanSheetLoading(false);
				return;
			}

			const url = res.data?.url;
			if (!url) throw new Error('sin url');

			irAUnaWebDeFuera(url, () => setIbanSheetLoading(false));
		} catch {
			setIbanSheetError(
				ibanGateText(
					'individual.ibanGate.error',
					'No hemos podido abrir el formulario. Inténtalo de nuevo.'
				)
			);
			setIbanSheetLoading(false);
		}
	}, [token, userTransfers, selectedPlanDisplayName, ibanGateText]);

	/**
	 * Al volver con el botón «atrás» del navegador, el CTA se quedaba muerto.
	 *
	 * 🔴 `setIbanSheetLoading(false)` solo estaba en el `catch`: en el camino bueno se da por hecho
	 * que `window.location.assign` se lleva la página y no vuelve. Pero con «atrás» sí vuelve, y
	 * vuelve desde la caché de retroceso del navegador, que restaura la página tal cual estaba —con
	 * `loading` congelado en `true`. El CTA es `disabled={loading}`, así que quedaba girando su
	 * ruedecita sin responder, y la única salida era recargar entera.
	 *
	 * Visto en Android, donde esa caché se usa mucho más que en escritorio. `persisted` distingue
	 * la restauración de una carga normal, en la que el estado ya nace limpio.
	 */
	useEffect(() => {
		const alVolver = (evento: PageTransitionEvent) => {
			if (evento.persisted) setIbanSheetLoading(false);
		};
		window.addEventListener('pageshow', alVolver);
		return () => window.removeEventListener('pageshow', alVolver);
	}, []);

	/**
	 * La vuelta del mini-KYC. El IBAN vive en el expediente de Supabase hasta
	 * que `sync-profile/me` lo copia a comparsa, así que hay que esperar a ese
	 * sync ANTES de refrescar: si no, el usuario se encontraría otra vez el
	 * gate que acaba de completar.
	 */
	const returningFromIbanKyc =
		typeof window !== 'undefined' &&
		new URLSearchParams(window.location.search).get(IBAN_GATE_RETURN_PARAM) === '1';

	const handleIbanKycSynced = useCallback(() => {
		// El perfil ya tiene la cuenta: se recarga el usuario para que el gate
		// deje de bloquear, y se limpia el marcador de la URL.
		setIbanSheetOpen(false);
		refreshCurrentUser();
		window.history.replaceState({}, '', window.location.pathname);
	}, [refreshCurrentUser]);

	const {
		syncing: ibanKycSyncing,
		failed: ibanKycFailed,
		pendingAmount: ibanKycPendingAmount
	} = useIbanGateReturn({
		token,
		returning: returningFromIbanKyc,
		onDone: handleIbanKycSynced
	});

	/**
	 * Al volver del mini-KYC, el slider recupera el importe que el usuario había
	 * elegido antes de irse — pero NO se escribe solo.
	 *
	 * Retomar es dejarle donde estaba, no decidir por él: la aportación se
	 * configura cuando la confirma, igual que la primera vez. Escribirla sin un
	 * clic suyo sería una sorpresa con su dinero de por medio.
	 */
	useEffect(() => {
		if (ibanKycPendingAmount === null) return;
		resumedAmountWins.current = true;
		setMonthlyContribution(ibanKycPendingAmount);
	}, [ibanKycPendingAmount]);

	/**
	 * Si el IBAN no llegó a comparsa, el desvío no ha servido: se vuelve a
	 * abrir la pantalla explicándolo. Dejarle seguir le llevaría al mismo error
	 * del que veníamos.
	 */
	useEffect(() => {
		if (!ibanKycFailed) return;
		setIbanSyncDismissed(false);
		setIbanSheetOpen(true);
		setIbanSheetError(
			ibanGateText(
				'individual.ibanGate.errorSync',
				'No hemos podido recuperar tu cuenta bancaria. Vuelve a intentarlo.'
			)
		);
	}, [ibanKycFailed, ibanGateText]);

	const oneTimePlanCatalogId = personalPlanConfig?.planCatalogId ?? undefined;

	/**
	 * ¿Puede aportar hoy? Lo dice el backend en `/limits`, pero cuando ese dato NO
	 * está disponible manda lo que sabemos de esta sesión.
	 *
	 * Sin esto había un agujero real: al reabrir el modal se refresca el margen, y
	 * si esa petición falla `oneTimeLimits` se pone a `null` → `canContributeToday`
	 * vuelve a `undefined` → fail-open → el usuario puede confirmar una segunda
	 * aportación que el backend va a rechazar. Justo el bug que esto arregla.
	 *
	 * El dato fresco manda sobre el recuerdo a propósito: si el backend dice que
	 * puede, puede. Si no, una sesión abierta pasada la medianoche se quedaría
	 * bloqueada el día siguiente.
	 */
	const canContributeToday = oneTimeLimits
		? oneTimeLimits.canContributeToday
		: contributedTodayInSession
			? false
			: undefined;

	/**
	 * Margen disponible EN ESTE PLAN, que el modal usa como importe máximo. Se
	 * pide al abrir el modal (no al montar la vista) para no gastar una llamada en
	 * cada visita al dashboard.
	 */
	const openOneTimeSheet = useCallback(async () => {
		// Sin IBAN ni se abre: el POST de la puntual revienta con un 500 porque
		// el body de Caser convierte un IBAN vacío. Se para antes de enseñar
		// importes.
		if (ibanGateLoading) return;

		if (ibanGateBlocked) {
			notifyIbanGateBlocked('one_time');
			setIbanSheetError(null);
			setIbanSyncDismissed(false);
			setIbanSheetOpen(true);
			return;
		}

		setIsOneTimeSheetOpen(true);

		if (!token) return;

		// Solo la apertura MÁS RECIENTE puede escribir el estado. Dos aperturas
		// seguidas dejan dos peticiones en vuelo y gana la que responda la última,
		// que puede ser la vieja: entonces un `canContributeToday` obsoleto
		// reactivaría el botón. Mismo patrón que la bandera de cancelación del
		// efecto que resuelve el plan.
		const requestId = ++oneTimeLimitsRequestRef.current;
		const isStale = () => requestId !== oneTimeLimitsRequestRef.current;

		setLoadingOneTimeLimits(true);
		try {
			// Mismo plan que viajará en la aportación: cada plan tiene su tope y su
			// propio consumo, así que pedir el margen de otro enseñaría un máximo
			// que la propia aportación rechazaría.
			const response = await getOneTimeContributionLimits(token, oneTimePlanCatalogId);
			if (isStale()) return;
			setOneTimeLimits(response.data ?? null);
		} catch (error) {
			if (isStale()) return;
			// Que no se sepa el margen no puede impedir aportar: sin él, el modal
			// no limita por arriba y es el backend quien valida al confirmar.
			console.error('Error fetching one-time contribution limits:', error);
			setOneTimeLimits(null);
		} finally {
			if (!isStale()) setLoadingOneTimeLimits(false);
		}
		// `ibanGateBlocked` va en las dependencias: sin él el callback se queda con
		// el valor del primer render —cuando la flag aún no ha resuelto— y el gate
		// no llega a aplicarse nunca.
	}, [token, oneTimePlanCatalogId, ibanGateBlocked, ibanGateLoading, notifyIbanGateBlocked]);

	const handleOneTimeContribution = useCallback(
		async (amount: number) => {
			if (!token) return;

			// El gate va también AQUÍ, no solo en la apertura del modal: este es el
			// POST que mueve el dinero.
			//
			// ⚠️ Defensa en profundidad, HOY INALCANZABLE y por eso sin test: con
			// `openOneTimeSheet` esperando a que la flag resuelva, no queda ningún
			// camino que abra el sheet y luego pase a bloqueado. Se deja porque el
			// coste es nulo y el día que aparezca otra vía de apertura —o que el
			// IBAN pueda cambiar dentro de la sesión— esto es lo único que separa
			// al usuario del 500.
			if (ibanGateLoading) return;
			if (ibanGateBlocked) {
				notifyIbanGateBlocked('one_time', amount);
				setIsOneTimeSheetOpen(false);
				return;
			}

			try {
				const response = await createOneTimeContribution(token, amount, oneTimePlanCatalogId);

				// Ya ha gastado su aportación de hoy y lo sabemos de primera mano. Se
				// recuerda para que un `/limits` que falle al reabrir el modal no
				// devuelva el fail-open y le deje intentar una segunda.
				setContributedTodayInSession(true);

				sonnerToast.success(
					translate('individual.oneTimeContribution.successTitle') || 'Aportación registrada',
					{
						description: (
							translate('individual.oneTimeContribution.successDescription') ||
							'Tu aportación puntual de {amount} se ha enviado a la gestora. Aparecerá en tus aportaciones cuando la confirme.'
						).replace('{amount}', fmtEur(amount))
					}
				);

				// Aviso NO bloqueante: la aportación se ha hecho, pero con las
				// mensuales que quedan por cobrarse se pasaría del tope fiscal. Va en
				// un toast aparte para no diluir la confirmación.
				if (response.data?.warning) {
					sonnerToast.success(
						translate('individual.oneTimeContribution.warningTitle') || 'Ojo con tu límite anual',
						{
							description:
								translate('individual.oneTimeContribution.warningScheduled') ||
								'Con tus aportaciones mensuales pendientes superarás el límite fiscal de este año. Puedes bajar tu aportación mensual cuando quieras.'
						}
					);
				}

				// NO se navega a Aportaciones: esa vista solo muestra los movimientos
				// que Caser ha CONFIRMADO, así que la aportación no aparece ahí hasta
				// el siguiente sync. Llevarle allí le enseñaría una lista sin su
				// aportación —o un "aún no hay aportaciones"— justo después de
				// decirle que se ha registrado.
			} catch (error) {
				sonnerToast.error(
					translate('individual.oneTimeContribution.errorTitle') ||
						'No se pudo registrar la aportación',
					{
						description: translateOneTimeContributionError(error, translate, {
							remaining: oneTimeLimits?.remaining
						})
					}
				);
				// Rethrow para que el sheet sepa que no debe cerrarse
				throw error;
			}
		},
		[
			token,
			toast,
			translate,
			oneTimeLimits,
			oneTimePlanCatalogId,
			ibanGateBlocked,
			ibanGateLoading,
			notifyIbanGateBlocked
		]
	);

	const handleChangeContributionDay = useCallback(
		async (day: number) => {
			if (!token || !adhesionMode) return;
			try {
				await updateB2CContributionDay(
					token,
					day,
					adhesionMode,
					// Ver el comentario de `updatePeriodicalContribution`.
					personalPlanConfig?.id
				);
				setContributionDay(day);
				const next = calculateNextIndividualContributionDate({
					today: new Date(),
					day,
					periodicity: 'MONTHLY'
				});
				if (next) {
					setNextContributionDate(
						next.toLocaleDateString(langCode === 'en' ? 'en-GB' : 'es-ES', {
							day: 'numeric',
							month: 'long',
							year: 'numeric'
						})
					);
				}
			} catch (error) {
				const message = error instanceof Error ? error.message : 'UNKNOWN_ERROR';
				toast({
					variant: 'destructive',
					title:
						translate('individual.changeContributionDay.errorTitle') || 'No se pudo cambiar el día',
					description:
						translate(`individual.changeContributionDay.error.${message}`) ||
						translate('individual.changeContributionDay.errorGeneric') ||
						'Inténtalo de nuevo en unos minutos.'
				});
				// Rethrow para que el sheet sepa que no debe cerrarse
				throw error;
			}
		},
		// `personalPlanConfig` en dependencias por el mismo motivo que en
		// `handleConfigureContribution`: sin él, el handler escribiría sin plan.
		[token, langCode, toast, translate, adhesionMode, personalPlanConfig]
	);

	/**
	 * 🔴 La respuesta que llega tarde NO puede pisar a la que llegó después.
	 *
	 * El mínimo de 2 s entre consultas evita el par de eventos que se disparan juntos al volver a
	 * la pestaña, pero no es un guard de concurrencia: en cuanto una respuesta tarda más de eso,
	 * hay dos peticiones en vuelo y vuelven en cualquier orden. Sin esto, alguien que sale y
	 * entra dos veces mientras le aprueban el KYC podía ver desaparecer la tarjeta —llega la
	 * respuesta nueva, `APPROVED`— y REAPARECER acto seguido, porque la petición vieja contesta
	 * después con el `PENDING_REVIEW` de antes. Justo el fallo que esto venía a arreglar, pero al
	 * revés, y arrastrando con él los pasos de destino y firma, que también leen `kycStatus`.
	 *
	 * Mismo patrón que `oneTimeLimitsRequestRef` en este archivo: solo escribe la última que se
	 * pidió, tarde o pronto que conteste.
	 */
	const refrescarEstadoKyc = useCallback(async () => {
		if (!token) return;
		ultimaConsultaKyc.current = Date.now();
		const peticion = ++consultaKycEnCurso.current;
		try {
			const response = await getKycStatus(token);
			if (peticion !== consultaKycEnCurso.current) return;
			if (response.success && response.data) {
				setKycStatusData(response.data);
			}
		} catch (error) {
			console.error('Error loading KYC status:', error);
		}
	}, [token]);

	useEffect(() => {
		refrescarEstadoKyc();
	}, [refrescarEstadoKyc]);

	/**
	 * 🔴 Volver a preguntar por el KYC al regresar a la pestaña.
	 *
	 * `handleStartKyc` abre la verificación con `window.open(url, '_blank')`: el KYC ocurre en
	 * OTRA pestaña y ésta se queda viva, montada y con el estado de antes. El efecto de arriba
	 * solo depende de `[token]`, así que no volvía a preguntar nunca: Marisa hizo el KYC entero
	 * en DEV, volvió aquí y se encontró la misma tarjeta pidiéndole que se verificara (30/09/26).
	 *
	 * Dos eventos, no uno: al cambiar de pestaña salta `visibilitychange`, pero si el KYC se
	 * abrió en otra VENTANA esta pestaña nunca se oculta y lo único que vuelve es el `focus`.
	 *
	 * Se deja de escuchar en cuanto el KYC llega a un estado final: `APPROVED` ya no cambia, y
	 * `NOT_REQUIRED` es que a esta persona no le toca. En el resto —incluido `PENDING_REVIEW`,
	 * que es el que se resuelve solo— cada vuelta comprueba si hay novedades, con un mínimo de
	 * 2 s entre consultas porque los dos eventos se disparan juntos al volver de una pestaña.
	 */
	const kycEnEstadoFinal =
		kycStatusData?.kycStatus === 'APPROVED' || kycStatusData?.kycStatus === 'NOT_REQUIRED';

	useEffect(() => {
		if (!token || kycEnEstadoFinal) return;

		const alVolver = () => {
			if (document.visibilityState !== 'visible') return;
			if (Date.now() - ultimaConsultaKyc.current < 2000) return;
			refrescarEstadoKyc();
		};

		document.addEventListener('visibilitychange', alVolver);
		window.addEventListener('focus', alVolver);
		return () => {
			document.removeEventListener('visibilitychange', alVolver);
			window.removeEventListener('focus', alVolver);
		};
	}, [token, kycEnEstadoFinal, refrescarEstadoKyc]);

	// Carga los datos de adhesión (saldo/plan) en cuanto se conoce el tipo de usuario
	// (adhesionMode), SIN exigir un traspaso 'completed'. Un autónomo puede tener un
	// plan activo abierto directamente (sin transfer en chirigota), igual que hacía la
	// vista vieja (MiBeneficioV2 pedía getAdhesionDetails incondicionalmente). Si no hay
	// plan activo, el backend devuelve vacío → hasActivePlan=false → estado de apertura.
	useEffect(() => {
		let cancelled = false;
		fetchAdhesionDetails(() => cancelled);
		return () => {
			cancelled = true;
		};
	}, [fetchAdhesionDetails]);

	// Máximo legal de aportación del autónomo (€/mes) para el slider. Lo calcula el
	// backend (mismo origen que usaba MiBeneficioV2). Solo para autónomos; los
	// individuales puros mantienen el default del slider.
	//
	// 🔴 Se mira el PLAN que se está viendo (`adhesionMode`), no el flag de la
	// persona. Un autónomo con un plan individual además del PPES abre esta misma
	// pantalla para ese plan, y ahí el tope legal es otro (1.500 € frente a los
	// 4.250/5.750 € del PPES). Con el flag se le ofrecía el tope de autónomo sobre
	// un plan individual, y `handleConfigureContribution` escribe la aportación
	// contra ESE plan: una periódica por encima de su límite legal.
	//
	// El `undefined` del else no es limpieza cosmética: al cambiar de plan en el
	// selector, un tope viejo en estado seguiría gobernando el slider del plan
	// nuevo.
	// Extraído a callback para poder recalcularlo tras declarar el plan externo: el tope
	// lo computa el backend restando esa declaración, así que un dato nuevo lo cambia.
	const fetchFreelanceMaxContribution = useCallback(() => {
		if (adhesionMode !== 'FREELANCE') {
			setFreelanceMaxContribution(undefined);
			return;
		}
		if (!token) return;
		// Fase 4 multi-cuenta (fix 21-jul): mode explícito para que un autónomo
		// con ≥2 filas (ej. autónomo + plan individual Horos) reciba el límite
		// de SU plan de autónomo, no "el" plan por defecto (más antiguo+activo).
		calculateMaxAportationsInfo(token, 'FREELANCE')
			.then((res) => {
				if (!res?.success || !res.data) return;
				const data = res.data as MaxAportationsInfoFreelanceOutput;
				if (typeof data.maxFreelanceContributionEndOfNextPeriod === 'number') {
					setFreelanceMaxContribution(data.maxFreelanceContributionEndOfNextPeriod);
				}
			})
			.catch(() => {});
	}, [token, adhesionMode]);

	useEffect(() => {
		fetchFreelanceMaxContribution();
	}, [fetchFreelanceMaxContribution]);

	// Plan individual externo declarado: alimenta el tramo "Plan individual" de la
	// tarjeta de límites y el CTA de registro. Reutilizable para refrescar tras declarar.
	const fetchIndividualPlan = useCallback(() => {
		if (!token) return;
		getIndividualPensionPlan(token)
			.then((res) => {
				setIndividualPlan(res?.success && res.data ? res.data : null);
			})
			.catch(() => {});
	}, [token]);

	useEffect(() => {
		fetchIndividualPlan();
	}, [fetchIndividualPlan]);

	// Aportaciones para la gráfica de evolución (acumulado mensual del año). Se trae
	// todo y se agrupa por año en cliente (mismo patrón que la pestaña Aportaciones).
	useEffect(() => {
		if (!token) return;
		getCalculatorsUserAportations(token, { page: 1, limit: 120, productCodes: planProductCodes })
			.then((res) => {
				if (res) setChartContributions(res);
			})
			.catch((error) => console.error('Error fetching contributions for chart:', error));
	}, [token, planProductCodes]);

	const chartMonths = useMemo(() => chartContributions?.items ?? [], [chartContributions]);

	// Años con aportación propia (de mayor a menor) — el selector de la gráfica solo
	// ofrece estos.
	const availableChartYears = useMemo(() => {
		const years = new Set<number>();
		chartMonths.forEach((m) => {
			const hasPropia = m.contributions.some(
				(c) => classifyContribution(c.typeContribution) === ContributionCategoryEnum.PARTICIPANT
			);
			if (hasPropia) years.add(parseInt(m.month.slice(0, 4), 10));
		});
		return Array.from(years).sort((a, b) => b - a);
	}, [chartMonths]);

	// Si el año de la gráfica no tiene datos, saltamos al más reciente que sí.
	useEffect(() => {
		if (availableChartYears.length > 0 && !availableChartYears.includes(chartYear)) {
			setChartYear(availableChartYears[0]);
		}
	}, [availableChartYears, chartYear]);

	// Aportación propia por mes natural del año (empresa = 0; individual/autónomo no
	// tiene aportación de empresa). Importes mensuales + `cumulative` → el componente
	// acumula. Se dibuja hasta el ÚLTIMO mes CON aportación real: no pintar meses
	// vacíos finales (p. ej. el mes en curso sin aportación todavía), porque la barra
	// acumulada plana confunde (parece una aportación cuando no la hay). Los meses
	// intermedios sin aportación sí se dibujan (la acumulada se mantiene plana, correcto).
	const chartData = useMemo<MonthlyContributionData[]>(() => {
		const propiaByMonth = new Map<number, number>();
		chartMonths
			.filter((m) => m.month.startsWith(`${chartYear}-`))
			.forEach((m) => {
				const monthIdx = parseInt(m.month.slice(5, 7), 10);
				const propia = m.contributions
					.filter(
						(c) => classifyContribution(c.typeContribution) === ContributionCategoryEnum.PARTICIPANT
					)
					.reduce((sum, c) => sum + (c.contributionAmount || 0), 0);
				propiaByMonth.set(monthIdx, propia);
			});
		const monthsWithData = Array.from(propiaByMonth.entries())
			.filter(([, propia]) => propia > 0)
			.map(([idx]) => idx);
		const maxMonth = monthsWithData.length > 0 ? Math.max(...monthsWithData) : 0;
		const result: MonthlyContributionData[] = [];
		for (let i = 1; i <= maxMonth; i++) {
			result.push({
				month: CHART_MONTH_LABELS[i - 1],
				empresa: 0,
				propia: propiaByMonth.get(i) || 0
			});
		}
		return result;
	}, [chartMonths, chartYear]);

	// Aportación propia acumulada en el AÑO NATURAL en curso (para la tarjeta de
	// límites legales). Misma fuente y clasificación que la gráfica: solo
	// PARTICIPANT (no empresa ni traspasos), en euros.
	const contributedThisYear = useMemo(() => {
		const currentYear = new Date().getFullYear();
		return chartMonths
			.filter((m) => m.month.startsWith(`${currentYear}-`))
			.reduce(
				(total, m) =>
					total +
					m.contributions
						.filter(
							(c) =>
								classifyContribution(c.typeContribution) === ContributionCategoryEnum.PARTICIPANT
						)
						.reduce((sum, c) => sum + (c.contributionAmount || 0), 0),
				0
			);
	}, [chartMonths]);

	// Fetch origin plan type (EMPLEO / INDIVIDUAL) for the process title
	useEffect(() => {
		const transfer = userTransfers.find(
			(t) =>
				(t.status === 'requested' || t.status === 'draft' || t.status === 'in_progress') &&
				!isNewPlanTransfer(t)
		);
		if (!token || !transfer?.DGSFP_code) return;
		getOriginPlanType(transfer.DGSFP_code, token)
			.then(setOriginPlanType)
			.catch(() => {});
	}, [userTransfers, token]);

	// Traspaso real (no "nuevo plan") para el que se pide la comparación. Determina
	// tanto qué comparar como si hay que esperar (gate) antes de montar ExplorationView.
	// Con el plan en proceso, la petición la elige su `kind` (ver `pendingPersonalPlan`).
	const funnelPrimaryTransfer = pendingPersonalPlan
		? resolveFunnelPrimaryTransfer(userTransfers)
		: undefined;
	const isComparableTransfer = (t: PensionPlanTransfer) =>
		(t.status === 'requested' || t.status === 'draft') && !isNewPlanTransfer(t);
	const comparisonTransfer = funnelPrimaryTransfer
		? [funnelPrimaryTransfer].find(isComparableTransfer)
		: userTransfers.find(isComparableTransfer);

	// Fetch comparison data for the primary transfer
	useEffect(() => {
		if (!token || !comparisonTransfer) return;
		setComparisonLoaded(false);
		getTransferComparison(token, comparisonTransfer.id)
			.then((res) => {
				// Sin datos del plan del usuario no se estima nada (ni media de empleo): la
				// rentabilidad la decide comparsa en `/plan-returns` y, sin datos, se oculta su curva.
				if (res.success && res.data) setComparisonData(res.data);
			})
			.catch(() => {})
			// Settled (con datos, sin datos o error): libera el gate del render.
			.finally(() => setComparisonLoaded(true));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token, comparisonTransfer?.id]);

	const metrics = calculatePensionPlanMetrics({
		adhesionDetails,
		userType: UserType.INDIVIDUAL,
		transfers: userTransfers,
		planTypology: 'INDIVIDUAL',
		// Cost-basis directo (sin gate): en B2C el plan individual es el que mostramos.
		externalTransferCostBasis: costBasis?.externalTransferCostBasis ?? 0,
		totalRescued: costBasis?.totalRescued ?? 0
	});

	// Equivalente de `isRetiredBeneficiary` (empleo) para un cliente 100%
	// individual, sin relación laboral de por medio. Ya no se decide por email:
	// sale del backend, y sólo con una solicitud CONFIRMADA por un motivo que
	// corta aportaciones — pedir un rescate no es haberlo tramitado.
	const {
		userEppConfigId: individualEppConfigId,
		activeStoppingReason: individualStoppingReason,
		annuityState: individualAnnuityState,
		pendingAnnuity: individualPendingAnnuity,
		refresh: refreshIndividualWithdrawals
	} = useWithdrawalRequests(
		token,
		adhesionMode ?? 'INDIVIDUAL',
		// El id solo viaja con un tipo personal conocido (ver `isPersonalPlanType`).
		isPersonalPlanType(selectedPlanType) ? selectedPersonalPlanId : null
	);
	// Puerta de rescates: cerrada por defecto mientras no sepamos por Caser
	// quién tiene algo rescatable.
	const { enabled: rescuesEnabled } = useRescuesEnabled(token, companyId ?? null, userId);
	// La sección de traspasos de Proa, detrás de su flag mientras se prueba en PROD con unas
	// pocas personas (chirigota#586). Apagada, Inicio queda como estaba: sin tarjeta de acción.
	const { enabled: seccionTraspasos } = useSeccionTraspasosEnabled(
		token,
		companyId ?? null,
		userId
	);
	const isRetiredBeneficiaryIndividual =
		rescuesEnabled && reasonStopsContributions(individualStoppingReason);

	// UX SCAFFOLD — `getAdhesionDetails('INDIVIDUAL')` acaba llamando al SOAP
	// real de Caser (vested-rights, `caserEmploymentPensionsService.getAdhesionDetails`
	// en comparsa), que devuelve vacío/0 para el NAPI ficticio de estos
	// usuarios de prueba — MISMO gap que `TEST_EMPLOYEE_FINANCIALS` en empleo
	// (María/Ricardo), no algo nuevo de individual. Por eso `adhesionDetails`
	// se queda vacío y `totalSaved`/`hasActivePlan` no reflejan el dinero real
	// ya sembrado en BBDD (ver seeds en `comparsa/scripts/`) — se fija a mano,
	// pero el importe coincide EXACTO con lo sembrado (no es un número
	// inventado ni reutilizado de otro contexto).

	// UX SCAFFOLD — Sonia: equivalente individual de Ricardo en empleo (ACTIVA,
	// nunca ha rescatado — a diferencia de Laura, NO pivota a la vista de
	// beneficiaria: mismo Inicio que cualquier usuaria individual normal, solo
	// cambia este importe por el mismo gap de Caser SOAP de arriba). Convertida
	// de empleo a individual (ver `comparsa/scripts/2026-07-30-convert-sonia-
	// employee-to-individual.sql`) — el importe es EXACTO al histórico
	// sembrado ahí (6.000€ traspaso + 8×200€ aportado, sin rescate), no el
	// total que tenía como empleada.

	// Siempre el saldo real. Antes había importes fijos por email de QA: con el
	// estado saliendo del backend, cualquiera podía acabar viendo el saldo de
	// otra persona.
	const totalSaved = metrics.totalSaved;
	const actionsProduct = metrics.actionsProduct;
	const bondsProduct = metrics.bondsProduct || { numParticipations: 0, netAssetValue: 0 };
	const performancePct = metrics.performance.percentage;
	const performanceAmt = metrics.performance.amount;
	const isPerformanceReliable = metrics.isPerformanceReliable;
	const roi = metrics.roi;
	const totalBenefitInEuros = metrics.totalBenefitInEuros;
	const totalContributions = metrics.sumUserContributions / 100;
	const totalTransferred = metrics.totalTransferred;
	const totalRescatado = costBasis?.totalRescued ?? 0;

	// Guard multi-plan. El endpoint de aportaciones (getCalculatorsUserAportations) NO está
	// scopeado por plan: agrega TODAS las aportaciones del usuario en Arca. Para usuarios con
	// varios planes (p. ej. un plan de EMPLEO con aportación propia + este plan individual aún
	// sin aportaciones), eso contamina la gráfica de evolución y la barra de límites de ESTE
	// plan con las aportaciones del otro. La fuente fiable por-plan es adhesionDetails
	// (sumUserContributions, scopeada por adhesionMode) — la misma que muestra "Total
	// aportaciones al plan". Si este plan no tiene aportaciones propias, no pintamos ninguna
	// aquí (mostramos empty-state) en vez de las del otro plan.
	// TODO(multi-plan): cuando el backend scope-e las aportaciones por plan, eliminar el guard.
	const planHasOwnContributions = metrics.sumUserContributions > 0;

	// Fecha del valor del plan (DD/MM/YYYY), del primer detalle de adhesión: la de su valor
	// liquidativo, o la de la foto si aún no la tiene (product#74).
	const lastUpdateDate = formatValueDate(adhesionDetails[0]);

	// Aportaciones al plan individual EXTERNO declarado (€). Cuenta dentro del slot
	// individual de 1.500 € de la tarjeta de límites.
	const externalPlanContributions =
		individualPlan?.hasIndividual && individualPlan.active ? individualPlan.contributionsMade : 0;
	const hasExternalPlan = !!(individualPlan?.hasIndividual && individualPlan.active);

	// Tips educativos (sin el de "company match", que no aplica a individual/autónomo).
	// El CTA lleva al simulador correspondiente (autónomos) o, si no hay simuladores
	// (individuales), hace scroll al slider de aportación.
	// UX SCAFFOLD (tarea Notion "Definimos MVP rescates") — igual que
	// MiBeneficioV2.tsx (empleo): quien ya rescató ve un dato sobre SU ahorro
	// (100% rescatable) en vez de tips sobre seguir aportando.
	const educationalTips = useMemo<(TipData & { action: string })[]>(
		() =>
			isRetiredBeneficiaryIndividual
				? [
						{
							icon: Wallet,
							title: translate('rescue.individualDashboard.tipTitle'),
							// Sin importe: hoy no hay ningún dato de Caser que diga cuánto
							// tiene rescatable, así que no se le promete ninguna cifra.
							text: translate('rescue.individualDashboard.tipText'),
							cta: translate('rescue.individualDashboard.tipCta'),
							action: 'rescue'
						}
					]
				: [
						{
							icon: Receipt,
							title: translate('benefit.tips.taxTitle'),
							text: translate('benefit.tips.taxText'),
							cta: translate('benefit.tips.taxCta'),
							action: 'fiscal'
						},
						{
							icon: TrendingUp,
							title: translate('benefit.tips.compoundTitle'),
							text: translate('benefit.tips.compoundText'),
							cta: translate('benefit.tips.compoundCta'),
							action: 'simulate'
						}
					],
		[translate, isRetiredBeneficiaryIndividual, totalSaved]
	);

	const kycStatus = kycStatusData?.kycStatus || 'NOT_REQUIRED';
	// «Ya está dado de alta en un plan», sin mirar si el dinero llegó.
	const hasEnrolledPlan = adhesionDetails.length > 0 && !loadingAdhesionDetails;
	// Quien tiene plan lo ve, aunque marque 0 € porque su dinero está en camino.
	//
	// Antes se le escondía: Caser crea la adhesión al dar de alta tras el KYC, y
	// mientras el traspaso no traía el dinero la vista se sustituía por el hero de
	// «Apertura de nuevo plan». Con los segundos traspasos eso le pasaba a un
	// cliente que YA tenía plan solo por pedirnos traer más dinero, y no hay forma
	// fiable de distinguir desde aquí «el traspaso de mi alta» de «uno que pido
	// después»: la fecha de alta no viaja (`adhesionDetails[0].day` es la última
	// valoración) y el `kind` no separa funnel de plataforma.
	//
	// Así que la regla es la simple: tienes plan → lo ves, y el estado de cada
	// traspaso vive en Trámites, con una tarjeta por petición (decisión de
	// producto, 11/09/26; el diseño definitivo lo está haciendo Elena).
	const hasActivePlan = hasEnrolledPlan || debugActivePlan || isRetiredBeneficiaryIndividual;
	// Lo que se enseña en Trámites. Con el plan ya operativo se cae la petición
	// del ALTA: su tarjeta sale con los cuatro pasos en verde y sin datos al
	// lado, debajo del traspaso que sí está en curso, y no cuenta nada que el
	// bloque del plan no cuente mejor. Mientras no hay plan sí se enseña: ahí es
	// EL proceso que la persona está siguiendo (Marisa, 11/09/26).
	/*
	 * 🔴 La decisión se toma UNA vez por traspaso y se congela, y esto no es un detalle.
	 *
	 * Si el filtro preguntara a `localStorage` en cada render, el efecto de abajo marcaría el
	 * traspaso como visto justo después de pintarlo y el siguiente render lo haría desaparecer
	 * —delante de la persona, en el mismo instante en que se estaba enterando de que su
	 * traspaso ha terminado—. Lo que pide el diseño es «se ve en ESTA visita y ya no en la
	 * siguiente», así que lo que decide es el estado que había AL LLEGAR.
	 *
	 * Lo cazó un test al quedarse sin su línea de tiempo; es el mismo patrón que ya usaba
	 * `TransferStatusCard` por dentro.
	 */
	const vistoAlLlegar = useRef<Record<string, boolean>>({});
	const yaEstabaVistoAlLlegar = (id: string) => {
		if (!(id in vistoAlLlegar.current)) {
			// `false` si no se puede leer: ante la duda, enseñar el traspaso. Ver la utilidad.
			vistoAlLlegar.current[id] = hasSeenTransferConfirmation(id, false);
		}
		return vistoAlLlegar.current[id];
	};

	const tramites = (
		hasActivePlan ? userTransfers.filter((t) => !isNewPlanTransfer(t)) : userTransfers
	).filter((t) => !(t.status === 'completed' && yaEstabaVistoAlLlegar(t.id)));

	/*
	 * 🔴 La otra mitad de «el traspaso completado pasa a Movimientos».
	 *
	 * Un traspaso que termina se ve UNA vez aquí —la visita en la que la persona se entera de
	 * que ya está— y a partir de la siguiente vive en Movimientos, que es su histórico
	 * permanente. Lo pide la tarea «Aterrizaje unificado» (bloque 2): «que un traspaso
	 * completado deje de verse en Inicio/Mi beneficio y pase a Movimientos, con marca de visto
	 * persistida por traspaso».
	 *
	 * Sin esto, Trámites acumulaba para siempre tarjetas de procesos terminados —los cuatro
	 * pasos en verde y sin nada que hacer— por encima de lo que sí está pasando. Y la marca de
	 * «visto» que ya existía no la leía nadie: estaba escrita la mitad de la función.
	 *
	 * La marca se pone DESPUÉS de pintar y no al filtrar: si se marcara en el mismo paso, la
	 * primera visita ya no lo vería y el aviso no existiría nunca. No hay backend que lo
	 * recuerde —el aviso duradero es el correo—, así que vive en `localStorage`.
	 */
	useEffect(() => {
		userTransfers
			.filter((t) => t.status === 'completed')
			.forEach((t) => markTransferConfirmationSeen(t.id));
	}, [userTransfers]);

	// Que el dinero haya llegado de verdad. Ya no es lo mismo que tener plan
	// activo, y hay sitios que necesitan justo esto y no aquello.
	// `> 0` y no `!== 0`: totalSaved es un float (participaciones × valor
	// liquidativo), así una valoración residual mínima no cuenta como dinero.
	const hasFundsInPlan = totalSaved > 0;

	/* 🔴 Aquí vivía `kycNeedsAction`, con `REJECTED` en la lista de «acción requerida». Estaba
	   declarada y sin usar desde hacía tiempo, y desde hoy además diría lo contrario que el resto
	   del archivo: un KYC rechazado NO es accionable —comparsa tampoco lo considera así
	   (`KYC_ACTIONABLE_STATUSES`)— y por eso su tarjeta no lleva botón. Se va, en vez de dejar una
	   definición muerta que el siguiente en leerla se creería. */

	const primaryTransfer = funnelPrimaryTransfer ?? resolvePrimaryTransfer(userTransfers);
	/** Las otras peticiones vivas del funnel: el plan que se elija también va en ellas. */
	const funnelSiblingTransfers = funnelPrimaryTransfer
		? liveFunnelEnrollments(userTransfers).filter((t) => t.id !== funnelPrimaryTransfer.id)
		: [];
	const isNewPlanPrimary = !primaryTransfer || isNewPlanTransfer(primaryTransfer);

	// Comparativa de rendimiento del traspaso: la rentabilidad de los dos planes la decide comparsa
	// (`/plan-returns`, periodo común; chirigota#576). En exploración no se pide: allí se pinta otra
	// vista que pide lo suyo. Sin plan de Arca resuelto o sin código DGSFP válido, nada que pedir.
	// El plan de Arca sale de la propia comparación (como en la exploración), para que nombre y
	// cifras vayan siempre juntos; sin ella, del traspaso.
	const comparisonArcaSlug =
		!isNewPlanPrimary && !isOnboardingExploration(primaryTransfer)
			? (
					(comparisonData && findArcaPlanById(comparisonData.arcaPlan.id)) ??
					arcaPlanFromTransfer(comparisonTransfer)
				)?.catalogSlug
			: undefined;
	const comparisonOriginDgsfp = originPlanCode(comparisonTransfer?.DGSFP_code);
	// Con su edad, los planes de ciclo de vida llegan con sus tramos (chirigota#592).
	const comparisonReturns = useTransferReturns(
		token,
		comparisonArcaSlug,
		comparisonOriginDgsfp,
		// Los años del funnel (los de la curva de la comparativa) o, si no, los de su edad.
		yearsToRetirementFor(comparisonTransfer?.metadata?.years_to_retirement, knownAge)
	);
	// Rentabilidad de cada plan de Arca (comparsa, una vez por sesión): la de su plan activo
	// alimenta la proyección de Inicio. `undefined` mientras llega.
	const arcaPlanReturns = useArcaPlanReturns(token, yearsToRetirementFromAge(knownAge));
	// Traspasos: lo que resuelve el plan cuando aún no hay uno elegido (más abajo).
	const transferForActivePlan = resolveActiveTransfer(userTransfers);
	const arcaPlanForActivePlan = arcaPlanFromTransfer(transferForActivePlan);
	// Su rentabilidad de comparsa (nunca la de ARCA_PLANS): la de su plan REAL; si aún no lo hay,
	// la del plan del traspaso. Si no viene en la lista (plan inactivo), se pide aparte.
	// `undefined` mientras llega; sin plan resuelto o sin datos, null.
	const activePlanReturn = useOwnPlanReturn(
		token,
		plansLoading
			? undefined
			: selectedPlanCatalogSlug ?? arcaPlanForActivePlan?.catalogSlug ?? null,
		yearsToRetirementFromAge(knownAge),
		arcaPlanReturns
	);

	/**
	 * Arranca el KYC en backend y devuelve la URL a abrir (o null si no arrancó). Punto único usado
	 * por handleProcessStarted (confirmar plan) y handleStartKyc (CTA del ProcessTimeline).
	 */
	const resolveKycUrl = useCallback(
		async (kycToken: string): Promise<string | null> => {
			try {
				const response = await startKyc(kycToken);
				if (response.success && response.data?.url) {
					return response.data.url;
				}
			} catch (error) {
				console.error('Error starting KYC:', error);
			}
			// El plan ya quedó bloqueado / el usuario cae en el ProcessTimeline con el botón de KYC;
			// avisamos por toast (visible aunque la vista cambie) de que la verificación no arrancó.
			toast({
				variant: 'destructive',
				title:
					translate('individual.process.kycStartError.title') ||
					'No pudimos iniciar la verificación de identidad',
				description:
					translate('individual.process.kycStartError.description') ||
					'Vuelve a intentarlo con el botón «Verificar identidad».'
			});
			return null;
		},
		[toast, translate]
	);

	/**
	 * Called by ExplorationView when the user confirms the plan: lock the plan backstage y arranca
	 * el KYC, devolviendo su URL para que ExplorationView redirija la pestaña que abrió en el click.
	 * Si el KYC no arranca (o no hay token), devolvemos null → la pestaña se cierra y el usuario cae
	 * en la plataforma, donde el ProcessTimeline muestra el paso "Verificar identidad".
	 */
	const handleProcessStarted = useCallback(async (): Promise<string | null> => {
		let kycUrl: string | null = null;
		if (token) {
			const target =
				funnelPrimaryTransfer ??
				userTransfers.find((t) => t.status === 'requested' || t.status === 'draft');
			if (target?.id) {
				try {
					// Persist selectedArcaPlanId if not already saved (resolves from metadata fallback)
					const resolvedId =
						target.selectedArcaPlanId ??
						(target.metadata?.selectedPlanId as string | undefined) ??
						findArcaPlanByName(target.metadata?.plan_name ?? '')?.id;
					const fixAndLock = async (transfer: PensionPlanTransfer) => {
						if (resolvedId && transfer.selectedArcaPlanId !== resolvedId) {
							await updateMyTransfer(token, transfer.id, { selectedArcaPlanId: resolvedId });
						}
						await lockTransferPlan(token, transfer.id);
					};
					// Con el plan en proceso, el plan se fija y se bloquea también en las otras
					// peticiones vivas del funnel. 🔴 Primero ellas y la principal la ÚLTIMA: si
					// una hermana falla, la principal sigue sin bloquear y la exploración vuelve
					// a salir para reintentar, en vez de dejar el alta suelta sin que nadie lo vea.
					await Promise.all(funnelSiblingTransfers.filter((t) => !t.planLockedAt).map(fixAndLock));
					await fixAndLock(target);
				} catch {
					// backstage lock — non-blocking, KYC step resolves separately
				}
			}
			// Arrancar el KYC para redirigir la pestaña ya abierta (misma llamada que el CTA del
			// ProcessTimeline vía handleStartKyc, pero aquí devolvemos la URL en vez de abrir nosotros).
			kycUrl = await resolveKycUrl(token);
		}
		loadUserTransfers();
		onFunnelRequestsChanged?.();
		return kycUrl;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		token,
		userTransfers,
		loadUserTransfers,
		resolveKycUrl,
		pendingPersonalPlan,
		onFunnelRequestsChanged
	]);

	/** Step 2 → KYC: open KYC onboarding URL. */
	const handleStartKyc = useCallback(async () => {
		if (!token) return;
		const url = await resolveKycUrl(token);
		if (url) {
			// No `window.open`: llega después de una llamada y en la app de iPhone no abriría nada.
			abrirEnElNavegador(url);
			loadUserTransfers();
		}
	}, [token, loadUserTransfers, resolveKycUrl]);

	// Loading
	if (isLoadingTransfers) {
		return (
			<div className="flex items-center justify-center h-96">
				<Loader2 className="w-8 h-8 animate-spin text-primary" />
			</div>
		);
	}

	// Exploration mode: solo para el onboarding a medias — un traspaso pedido
	// desde la plataforma se le parece (nace `requested` y sin plan bloqueado)
	// pero NO es un onboarding: ver `isOnboardingExploration`.
	const isInExplorationMode = isOnboardingExploration(primaryTransfer);
	if (isInExplorationMode && primaryTransfer) {
		const landingScenario = detectLandingScenario(primaryTransfer);
		// Gate: si este traspaso pide comparación y aún no ha resuelto, esperamos con el
		// loader en vez de montar ExplorationView en su versión parcial (solo "Tu
		// elección") y saltar luego a la completa. El "nuevo plan" no pide comparación
		// (comparisonTransfer undefined) → monta directo, sin esperar.
		if (comparisonTransfer && !comparisonLoaded) {
			return (
				<div className="flex items-center justify-center h-96">
					<Loader2 className="w-8 h-8 animate-spin text-primary" />
				</div>
			);
		}
		return (
			<ExplorationView
				transfer={primaryTransfer}
				scenario={landingScenario}
				translate={translate}
				userName={userFirstName}
				currentAge={knownAge}
				comparisonData={comparisonData}
				isFreelance={isFreelance}
				onProcessStarted={handleProcessStarted}
				siblingTransferIds={funnelSiblingTransfers.map((t) => t.id)}
				onPlanChanged={onFunnelRequestsChanged}
			/>
		);
	}

	const processTitle = (() => {
		if (isNewPlanPrimary) {
			return translate('individual.process.title.newPlan') || 'Apertura de nuevo plan';
		}
		if (originPlanType === 'EMPLEO') {
			return translate('individual.process.title.transferEmpleo') || 'Traspaso de plan de empleo';
		}
		if (originPlanType === 'INDIVIDUAL') {
			return (
				translate('individual.process.title.transferIndividual') || 'Traspaso de plan individual'
			);
		}
		return translate('individual.process.title.transfer') || 'Tu nuevo plan de pensiones';
	})();

	// TODO confirmar copy con Marisa
	const processSubtitle = (() => {
		if (!primaryTransfer) return translate('individual.dashboard.subtitle');
		const s = primaryTransfer.status;
		if (s === 'completed')
			return isNewPlanPrimary
				? translate('individual.process.subtitle.completedNewPlan') ||
						'Tu plan de pensiones ya está activo.'
				: translate('individual.process.subtitle.completed');
		if (s === 'in_progress') return translate('individual.process.subtitle.processing');
		const kycDone = kycStatus === 'APPROVED' || kycStatus === 'NOT_REQUIRED';
		if (kycDone) return translate('individual.process.subtitle.processing');
		return translate('individual.process.subtitle.kyc');
	})();

	/*
	 * 🔴 LA ACCIÓN QUE BLOQUEA EL TRASPASO, si la hay. Se pinta arriba del todo, antes que nada
	 * (decisión de Elena, 30/09/26): si a la persona le falta hacer algo, eso manda sobre
	 * cualquier cifra.
	 *
	 * Las tres condiciones se calculan con DATOS REALES. En la rama de origen dos de ellas eran
	 * `debugOverridesActive && debug…`, o sea que las tarjetas solo existían en depuración y
	 * ningún usuario las vio nunca. El dato para la firma ya estaba en master, resuelto en línea
	 * dentro del timeline; aquí se extrae para que lo usen los dos.
	 *
	 * Solo UNA a la vez, y en este orden, porque es el orden real del trámite: sin identidad no
	 * se puede firmar, y sin destino elegido el boletín saldría en blanco.
	 */
	/*
	 * 🔴 «Vivo» es lo mismo que para comparsa: `draft | requested | in_progress`. Antes esto solo
	 * descartaba `completed`, así que a quien la gestora le RECHAZÓ el traspaso se le pedía
	 * firmarlo o verificarse para un trámite muerto — y el backend lo rechaza con «Transfer
	 * request is not open for signature». `resolvePrimaryTransfer` cae en el más reciente cuando
	 * no hay ninguno vivo, así que un rechazado entra sin más.
	 */
	/*
	 * 🔴 Que sin traspaso vivo no haya tarjeta —tampoco la de KYC rechazado— es DELIBERADO
	 * (Marisa, 30/09/26): sin un trámite en marcha no hay nada que su identidad esté bloqueando.
	 * El hueco (comparsa no manda correo de rechazo) se acepta a sabiendas; si algún día se tapa,
	 * el sitio es ese correo, no esta condición.
	 */
	const traspasoVivo =
		primaryTransfer && ESTADOS_VIVOS.includes(primaryTransfer.status ?? '')
			? primaryTransfer
			: null;

	/*
	 * Qué tarjeta va arriba (identidad, destino o firma) y con qué estado. Los criterios —«aún no
	 * lo sé» no es «no hace falta», en revisión y rechazado sin botón, las cinco puertas de la
	 * firma— están en `accionPendienteDelTraspaso`, con sus motivos (chirigota#586).
	 */
	const accionPendiente = accionPendienteDelTraspaso({
		traspasoVivo,
		kycStatus,
		// Sin los firmados no se sabe si falta firmar: la lista vacía dice «no lo sé», no «sin
		// firmar». Igual que la tarjeta del traspaso (product#85).
		kycResuelto: kycStatusData !== null && firmadosResueltos,
		signedBulletinIds
	});
	/*
	 * 🔴 La acción pendiente, lo primero del contenido: si a la persona le falta hacer algo para
	 * que su traspaso avance, eso manda sobre cualquier cifra (decisión de Elena, 30/09/26).
	 * «Arriba del todo» es del contenido, no de la página: el título va siempre encima, como en
	 * Screens/Resumen Multicuenta de Proa (Marisa, 16/10/26). Por eso se pinta en un solo sitio
	 * según haya plan o no: debajo del hero, o debajo del título de la sección del plan.
	 */
	const tarjetaAccionPendiente = seccionTraspasos && accionPendiente && (
		<TarjetaAccionPendiente
			accion={accionPendiente}
			onVerificarIdentidad={handleStartKyc}
			onElegirDestino={() => setEligiendoDestino(true)}
			onFirmar={handleSignBulletin}
		/>
	);

	const completedTransfer = userTransfers.find((t) => t.status === 'completed');

	/*
	 * El estado real manda. Este atajo solo cubre el desfase de cuando el dinero YA está dentro y
	 * la fila aún no se ha marcado: por eso mira el saldo y no `hasActivePlan`, que desde hoy
	 * también es cierto con el plan a 0 €. Atarlo a `hasActivePlan` pintaría como terminado un
	 * traspaso que acaba de pedirse.
	 */
	const estadoDelTramite = (transfer: PensionPlanTransfer) =>
		hasFundsInPlan && transfer.id === (completedTransfer?.id ?? primaryTransfer?.id)
			? 'completed'
			: transfer.status;

	/*
	 * El timeline de un trámite de Inicio, el mismo con la tarjeta de Proa y sin ella. Con la tarjeta
	 * va sin el atajo (`conAtajo = false`): el saldo no dice si ESTE traspaso ha llegado —el plan puede
	 * tener dinero de antes o de una aportación—, así que se enseña el estado real, igual para todos
	 * los orígenes (Marisa, 26/10/2026).
	 */
	// `enTarjeta`: dentro de la tarjeta de Proa, sin el atajo de saldo (la tarjeta enseña el estado
	// real) y sin botón de identidad, que lo pide solo la tarjeta de arriba.
	const timelineDelTramite = (transfer: PensionPlanTransfer, enTarjeta = false) => (
		<ProcessTimeline
			hideKycCta={enTarjeta}
			currentStatus={enTarjeta ? transfer.status : estadoDelTramite(transfer)}
			kycStatus={kycStatus}
			processStarted={(transfer.processStarted as string | null) ?? transfer.createdAt ?? null}
			planLockedAt={transfer.planLockedAt ?? (transfer.processStarted as string | null) ?? null}
			kycCompletedAt={kycStatusData?.completedAt ?? null}
			executedAt={transfer.executedAt ?? null}
			activeStepHint={transfer.id === primaryTransfer?.id ? processSubtitle : undefined}
			isNewPlan={isNewPlanTransfer(transfer)}
			bulletinSignature={
				/* 🔴 Aquí se miraban DOS puertas (plataforma + destino elegido)
				   mientras la tarjeta de arriba miraba las cinco de comparsa.
				   Con el KYC sin aprobar, o con el DGSFP centinela, o en un alta
				   de plan nuevo, arriba no se decía nada y aquí aparecía un
				   botón «Firmar» que devolvía 400. Ahora las dos leen la misma
				   función.

				   Ya firmado se enseña SIEMPRE, sin pasar por las puertas: el
				   hito está cumplido y no necesita permiso. Además evita que el
				   paso parpadee mientras el estado del KYC está en vuelo. */
				signedBulletinIds.includes(transfer.id)
					? { signed: true }
					: puedeFirmarBoletin(transfer, kycStatus)
						? { signed: false }
						: undefined
			}
			translate={translate}
			onStepCtaClick={(step) => {
				// Firmar vale siempre: el paso solo existe en los
				// traspasos de plataforma, y ésos son de clientes
				// que YA tienen plan activo.
				if (step === 'sign') {
					handleSignBulletin(transfer.id);
					return;
				}
				if (hasActivePlan) return;
				if (step === 'kyc') handleStartKyc();
				if (step === 'completed') {
					activePlanRef.current?.scrollIntoView({
						behavior: 'smooth',
						block: 'start'
					});
				}
			}}
		/>
	);

	/*
	 * Con la flag `seccion-traspasos`, Inicio solo enseña los traspasos NO terminados, cada uno en
	 * la tarjeta de Proa; los terminados viven en Movimientos (decisión de Marisa, chirigota#603).
	 */
	const tramitesVivos = tramites.filter((t) => ESTADOS_VIVOS.includes(t.status ?? ''));
	const kycDoneTarjeta = kycStatus === 'APPROVED' || kycStatus === 'NOT_REQUIRED';

	// El nombre del plan que se enseña: manda el ELEGIDO. El derivado del traspaso
	// solo se usa cuando no hay elección (un plan, o lista aún sin llegar), y si
	// tampoco hay, no se pinta la línea: mejor sin nombre que con uno inventado.
	const planTitleName = selectedPlanDisplayName ?? arcaPlanForActivePlan?.name ?? null;

	// Horizonte de proyección: hasta los 67 (compartido con el simulador).
	const yearsToRetirement = Math.max(1, 67 - currentAge);

	// No vale mirar solo el hostname: el WebView de la app también se llama `localhost`.
	const isLocalhost = esEntornoLocal();
	const debugStatuses = ['requested', 'in_progress', 'completed'] as const;

	// Sección Trámites — sin sentido para quien ya rescató: no tiene ningún traspaso pendiente ni
	// "aún no tienes planes" que aportar, solo puede rescatar.
	const conTramites =
		!isRetiredBeneficiaryIndividual && (hasActivePlan ? tramites.length > 0 : true);
	const seccionTramites = conTramites && (
		<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
			{/* 🔴 `userTransfers`, no `tramites`, y la diferencia importa desde que Trámites
			    oculta los completados ya vistos: con `tramites` vacío por haberlos
			    ocultado, a alguien que SÍ traspasó se le enseñaba «Aún no has traspasado
			    ningún plan» y un botón para traspasar. El estado vacío tiene que
			    preguntar si hay traspasos, no si quedan por enseñar. */}
			{userTransfers.length === 0 ? (
				/* Empty state */
				<Card className="border-dashed border-2 border-gray-200">
					<CardContent className="py-12 text-center">
						<Info className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
						<h3 className="text-lg font-semibold mb-2">
							{translate('bringPlan.dashboard.noPlans.title')}
						</h3>
						<p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
							{translate('bringPlan.dashboard.noPlans.description')}
						</p>
						<div className="flex gap-3 justify-center flex-wrap">
							<Button
								variant="outline"
								onClick={() => {
									// TODO: Implementar apertura de nuevo plan
								}}
							>
								{translate('bringPlan.dashboard.openNewPlan')}
							</Button>
							<Button
								className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5"
								onClick={() => setIsTransferModalOpen(true)}
							>
								<ArrowRightLeft className="w-4 h-4" />
								{translate('bringPlan.dashboard.transferFunds')}
							</Button>
						</div>
					</CardContent>
				</Card>
			) : seccionTraspasos ? (
				<div className="space-y-3">
					{tramitesVivos.map((transfer) => {
						const abierta = traspasoAbiertoEnInicio({
							transfer,
							kycStatus,
							// Sin los firmados tampoco se sabe si falta firmar: abrirla y plegarla al
							// llegar sería un parpadeo.
							kycResuelto: kycStatusData !== null && firmadosResueltos,
							signedBulletinIds
						});
						return (
							/* 🔴 La `key` lleva `abierta`: `defaultExpanded` solo se lee al montar, y el KYC y
							   los firmados llegan DESPUÉS que los traspasos. Sin esto la tarjeta se montaba
							   plegada y ya no se abría nunca. */
							<TarjetaTraspaso
								key={`${transfer.id}-${abierta}`}
								transfer={transfer}
								kycDone={kycDoneTarjeta}
								defaultExpanded={abierta}
							>
								{timelineDelTramite(transfer, true)}
								<TransferInfoCard
									transfer={transfer}
									translate={translate}
									hidePlanInfo={hasActivePlan}
								/>
							</TarjetaTraspaso>
						);
					})}
				</div>
			) : (
				/* Transfer process cards */
				<div className="space-y-6">
					{tramites.map((transfer, idx) => (
						<motion.div
							key={transfer.id}
							initial={{ opacity: 0, y: 12 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ delay: (idx + 1) * 0.06, duration: 0.35 }}
							className="grid grid-cols-1 lg:grid-cols-2 gap-6"
						>
							<div className="surface-card p-5">{timelineDelTramite(transfer)}</div>
							<TransferInfoCard
								transfer={transfer}
								translate={translate}
								hidePlanInfo={hasActivePlan}
							/>
						</motion.div>
					))}
				</div>
			)}

			{/* Añadir traspaso — solo sin plan activo. Con plan, la entrada es el banner de traspaso de
			    debajo de la evolución: aquí dependía de que hubiera trámites, y sin ninguno desaparecía
			    (product#80). */}
			{!hasActivePlan && userTransfers.length > 0 && (
				<div className="mt-4">
					<button
						className="surface-soft w-full text-muted-foreground font-heading font-medium text-base py-2.5 px-5 flex items-center justify-center gap-1.5 hover:opacity-80 transition-opacity"
						onClick={() => setIsTransferModalOpen(true)}
					>
						<span className="text-lg">+</span>
						{translate('individual.dashboard.addTransfer')}
					</button>
				</div>
			)}
		</motion.div>
	);
	// Con la flag y plan activo, los trámites suben justo debajo de las dos tarjetas principales, antes
	// de las cifras y los límites: quien está traspasando tiene que ver primero cómo va (chirigota#633).
	// La misma condición que monta `ActivePlanSection`: si no, mientras llega `isFreelance` los trámites
	// no saldrían en ningún sitio.
	const tramitesArriba =
		seccionTraspasos &&
		hasActivePlan &&
		isFreelance !== undefined &&
		!isRetiredBeneficiaryIndividual;

	return (
		<div className="individual-v2 -mx-4 -mt-4 min-h-screen">
			{/* DEBUG panel — solo en localhost, fuera del space-y-8 para no afectar spacing */}
			{isLocalhost && (
				<div className="fixed bottom-4 right-4 z-50 bg-black/80 text-white text-xs rounded-xl p-3 flex flex-col gap-2 backdrop-blur-sm">
					{primaryTransfer && (
						<div className="flex items-center gap-2">
							<span className="opacity-60">status:</span>
							{debugStatuses.map((s) => (
								<button
									key={s}
									onClick={() =>
										setUserTransfers((prev) =>
											prev.map((t) => (t.id === primaryTransfer.id ? { ...t, status: s } : t))
										)
									}
									className={`px-2 py-0.5 rounded-md transition-colors ${
										primaryTransfer.status === s
											? 'bg-white text-black font-semibold'
											: 'hover:bg-white/20'
									}`}
								>
									{s}
								</button>
							))}
						</div>
					)}
					<div className="flex items-center gap-2">
						<span className="opacity-60">plan activo:</span>
						<button
							onClick={() => setDebugActivePlan((v) => !v)}
							className={`px-2 py-0.5 rounded-md transition-colors ${
								debugActivePlan ? 'bg-white text-black font-semibold' : 'hover:bg-white/20'
							}`}
						>
							{debugActivePlan ? 'ON' : 'OFF'}
						</button>
					</div>
				</div>
			)}
			<div className="max-w-6xl mx-auto space-y-8 p-4 pt-12 lg:p-6 lg:pt-14">
				{traspasoVivo && (
					<OtherPlansSheet
						open={eligiendoDestino}
						transfer={traspasoVivo}
						token={token}
						plans={ARCA_PLANS}
						translate={translate}
						onClose={() => setEligiendoDestino(false)}
						onPlanChanged={() => {
							/* La hoja ya ha persistido el destino con `updateMyTransfer`. Se cierra y se
							   recargan los traspasos: con `selectedArcaPlanId` puesto, la tarjeta de arriba
							   pasa sola de «elegir destino» a «firmar». */
							setEligiendoDestino(false);
							loadUserTransfers();
						}}
					/>
				)}

				{/* Hero — Tipo de proceso (oculto cuando el plan ya está activo) */}
				{!hasActivePlan && (
					<motion.div
						initial={{ opacity: 0, y: -8 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.4 }}
					>
						<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
							<div className="flex flex-col gap-1 min-w-0">
								<h1 className="text-3xl lg:text-[40px] font-bold tracking-[-0.03em] font-heading leading-[1.1] text-foreground">
									{processTitle}
								</h1>
								{primaryTransfer && (
									<p className="text-base text-muted-foreground">{processSubtitle}</p>
								)}
							</div>
							{onOpenInviteModal && primaryTransfer && (
								<button
									onClick={onOpenInviteModal}
									className="btn-arca-primary self-start shrink-0 flex items-center gap-2 text-sm"
								>
									<Gift className="w-4 h-4" />
									Invita y gana
								</button>
							)}
						</div>
					</motion.div>
				)}

				{/* Sin plan activo, la acción pendiente va debajo del hero; con plan, debajo del título
				    de su sección (ver `tarjetaAccionPendiente`). */}
				{!hasActivePlan && tarjetaAccionPendiente}

				{/* Active plan section */}
				{/* Guard anti-flicker: no pintar el bloque de plan activo (slider + límites,
				    que dependen del tipo de usuario) hasta saber si es autónomo o individual.
				    Evita que un autónomo vea por un instante los topes/límites de individual. */}
				{hasActivePlan && isFreelance !== undefined && isRetiredBeneficiaryIndividual && (
					<div ref={activePlanRef} className="space-y-6">
						{/* Igual que María en empleo (MiBeneficioV2.tsx): quien ya rescató
						    sustituye por completo el bloque de "aportar" (ActivePlanSection,
						    con su slider de aportación) por uno centrado en rescatar. */}
						<div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
							<div>
								<h1 className="text-3xl lg:text-[40px] font-bold tracking-[-0.03em] font-heading leading-[1.1] text-foreground">
									{userFirstName && <span className="text-primary">{userFirstName}</span>}
									{userFirstName && ', '}
									{translate('rescue.individualDashboard.heroHave')}{' '}
									<span className="text-primary">{fmtEur(totalSaved)}</span>{' '}
									{translate('rescue.individualDashboard.heroSaved')}
								</h1>
								{planTitleName && (
									<p className="text-muted-foreground mt-1">
										{translate('rescue.individualDashboard.planPrefix')}{' '}
										<span className="font-medium text-foreground">{planTitleName}</span>
									</p>
								)}
							</div>
							<Button
								className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground shrink-0"
								onClick={() => setShowRescueDialog(true)}
							>
								<PiggyBank className="w-4 h-4" />
								{translate('rescue.individualDashboard.requestButton')}
							</Button>
						</div>
						{tarjetaAccionPendiente}
						<RescueRentaSimulatorV2
							userEppConfigId={individualEppConfigId}
							annuityState={individualAnnuityState}
							pendingAnnuity={individualPendingAnnuity}
							token={token}
							onSubmitted={() => refreshIndividualWithdrawals()}
							availableToRescue={totalSaved}
							email={userEmail}
							currentReason={individualStoppingReason}
							onOpenCalculator={() => setShowCalculatorModal(true)}
						/>
						{/* Mismas 3 cards que María en empleo (MiBeneficioV2.tsx): Total
						    ahorrado, Rendimiento y Total rescatado — sustituyen a "Total
						    aportaciones"/"Total rendimiento generado" de ActivePlanSection,
						    que no aportan nada a alguien que ya no aporta. */}
						<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
							<Card className="border border-border shadow-xs h-full">
								<CardContent className="p-5">
									<div className="flex items-start justify-between mb-3">
										<div className="w-10 h-10 rounded-xl flex items-center justify-center bg-primary/10">
											<Wallet className="w-5 h-5 text-primary" />
										</div>
									</div>
									<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
										{translate('rescue.individualDashboard.totalSavedLabel')}
									</p>
									<p className="text-2xl font-bold tracking-tight text-primary">
										{fmtEur(totalSaved)}
									</p>
								</CardContent>
							</Card>
							<Card className="border border-border shadow-xs h-full">
								<CardContent className="p-5">
									<div className="flex items-start justify-between mb-3">
										<div className="w-10 h-10 rounded-xl flex items-center justify-center bg-primary/10">
											<TrendingUp className="w-5 h-5 text-primary" />
										</div>
									</div>
									<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
										{translate('rescue.individualDashboard.performanceLabel')}
									</p>
									<p className="text-2xl font-bold tracking-tight text-primary">
										{isPerformanceReliable ? (performancePct >= 0 ? '+' : '') : ''}
										{isPerformanceReliable ? `${fmtNum(performancePct, 2)}%` : '--'}
									</p>
									{isPerformanceReliable && (
										<p className="text-xs text-muted-foreground mt-1">
											{performanceAmt >= 0 ? '+' : ''}
											{fmtEur(performanceAmt)}
										</p>
									)}
								</CardContent>
							</Card>
							<Card className="border border-border shadow-xs h-full">
								<CardContent className="p-5">
									<div className="flex items-start justify-between mb-3">
										<div className="w-10 h-10 rounded-xl flex items-center justify-center bg-primary/10">
											<PiggyBank className="w-5 h-5 text-primary" />
										</div>
									</div>
									<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
										{translate('rescue.individualDashboard.totalRescuedLabel')}
									</p>
									<p className="text-2xl font-bold tracking-tight text-primary">
										{fmtEur(totalRescatado)}
									</p>
								</CardContent>
							</Card>
						</div>
					</div>
				)}
				{hasActivePlan && isFreelance !== undefined && !isRetiredBeneficiaryIndividual && (
					<div ref={activePlanRef}>
						<ActivePlanSection
							accionPendiente={tarjetaAccionPendiente}
							traspasos={tramitesArriba ? seccionTramites : undefined}
							userName={userFirstName}
							arcaPlan={arcaPlanForActivePlan}
							planDisplayName={selectedPlanDisplayName}
							transfer={completedTransfer ?? primaryTransfer}
							totalSaved={totalSaved}
							lastUpdateDate={lastUpdateDate}
							yearsToRetirement={yearsToRetirement}
							planReturn={activePlanReturn}
							totalContributions={totalContributions}
							totalTransferred={totalTransferred}
							performancePct={performancePct}
							performanceAmt={performanceAmt}
							isPerformanceReliable={isPerformanceReliable}
							roi={roi}
							showRoi={false}
							totalBenefitInEuros={totalBenefitInEuros}
							actionsProduct={actionsProduct}
							bondsProduct={bondsProduct}
							monthlyContribution={monthlyContribution}
							maxContribution={freelanceMaxContribution}
							nextContributionDate={nextContributionDate}
							contributionDay={contributionDay}
							onConfigure={handleConfigureContribution}
							onChangeDayClick={() => setIsChangeDaySheetOpen(true)}
							onOneTimeContribution={canMakeOneTimeContribution ? openOneTimeSheet : undefined}
							limitsSection={
								<ContributionLimitsChartV2
									isFreelance={!!isFreelance}
									isIndividual={!isFreelance}
									employmentContributed={
										isFreelance && planHasOwnContributions ? contributedThisYear : 0
									}
									individualContributed={
										isFreelance
											? externalPlanContributions
											: (planHasOwnContributions ? contributedThisYear : 0) +
												externalPlanContributions
									}
									translate={translate}
								/>
							}
							translate={translate}
							inviteButton={
								onOpenInviteModal ? (
									<button
										onClick={onOpenInviteModal}
										className="btn-arca-primary flex items-center gap-2 text-sm shrink-0"
									>
										<Gift className="w-4 h-4" />
										Invita y gana
									</button>
								) : undefined
							}
						/>
					</div>
				)}

				{!tramitesArriba && seccionTramites}

				{/* Comparativa de rendimiento: solo con la rentabilidad de comparsa de los dos planes. Sin
				    la del plan del usuario no hay nada que comparar (nunca una tasa por defecto). */}
				{!isNewPlanPrimary &&
					comparisonTransfer &&
					comparisonReturns?.arca &&
					comparisonReturns.origin && (
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ delay: 0.35 }}
						>
							<PerformanceComparisonV2
								arca={comparisonReturns.arca}
								origin={comparisonReturns.origin}
								samePeriod={comparisonReturns.samePeriod}
								currentPlanName={comparisonData?.originPlan.manager}
								arcaPlanName={comparisonData?.arcaPlan.manager}
								{...transferProjectionInputs(comparisonTransfer, currentAge)}
							/>
						</motion.div>
					)}

				{/* Gráfica de evolución de aportaciones — justo debajo del bloque de
				    aportación. Sin barra de empresa (no aplica) y en verde individual. */}
				{hasActivePlan && (planHasOwnContributions ? availableChartYears.length > 0 : true) && (
					<ContributionsEvolutionChartV2
						data={planHasOwnContributions ? chartData : []}
						emptyState={!planHasOwnContributions}
						year={chartYear}
						cumulative
						hideCompany
						contributionColor="#0CA855"
						availableYears={planHasOwnContributions ? availableChartYears : []}
						onYearChange={setChartYear}
						onViewDetail={onNavigateToContributions}
						translate={translate}
					/>
				)}

				{/* Traer un plan de otra entidad: la entrada para pedir un traspaso con plan activo, haya o no
				    traspasos en curso. Primero lo que tiene (su plan y su evolución), después las acciones
				    (product#80). No sustituye al banner de plan externo de más abajo: ése es para declararlo. */}
				{hasActivePlan && !isRetiredBeneficiaryIndividual && (
					<motion.div
						initial={{ opacity: 0, y: 8 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.36 }}
					>
						<ExternalPlanCTA
							ctaTitle={translate('individual.transferCta.title')}
							ctaDescription={translate('individual.transferCta.description')}
							ctaButtonLabel={translate('individual.transferCta.button')}
							onDiscoverClick={() => setIsTransferModalOpen(true)}
						/>
					</motion.div>
				)}

				{/* Tip educativo con CTA al simulador (autónomos) o al slider (individuales) */}
				{hasActivePlan && (
					<EducationalTipV2
						tips={educationalTips}
						onCtaClick={(tip) => {
							const action = (tip as TipData & { action?: string }).action;
							if (onNavigateToSimulators && (action === 'fiscal' || action === 'simulate')) {
								onNavigateToSimulators(action === 'fiscal' ? 'fiscal' : 'planificador');
							} else {
								activePlanRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
							}
						}}
					/>
				)}

				{/* Registrar plan de pensiones individual externo — declara aportaciones a
				    otro plan para que cuenten en el slot individual (límites + ahorro fiscal).
				    Sin sentido para quien ya rescató: ya no aporta a ningún plan, ni a
				    este ni a uno externo. */}
				{hasActivePlan && !isRetiredBeneficiaryIndividual && (
					<motion.div
						initial={{ opacity: 0, y: 8 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.42 }}
					>
						<ExternalPlanCTAV2
							hasExternalPlan={hasExternalPlan}
							externalPlanAmount={externalPlanContributions}
							onConfigureClick={() => setShowExternalPlanFlow(true)}
							translate={translate}
						/>
					</motion.div>
				)}

				{/* Quick Actions */}
				<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
					<QuickActionsGridV2 />
				</motion.div>

				{/* Documento de especificaciones del plan — solo autónomos */}
				{isFreelance && regulationLink && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.45 }}
					>
						<Card className="border shadow-xs hover:border-primary/30 transition-colors">
							<CardContent className="p-4 flex items-center gap-4">
								<div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
									<FileText className="w-5 h-5 text-primary" />
								</div>
								<div className="flex-1 min-w-0">
									<p className="text-sm font-medium truncate">
										{translate('employmentPlan.doc.planSpecs')}
									</p>
									<p className="text-xs text-muted-foreground">
										{translate('employmentPlan.doc.planSpecsSubtitle')}
									</p>
								</div>
								<a href={regulationLink} target="_blank" rel="noopener noreferrer">
									<Button
										size="icon"
										variant="ghost"
										className="shrink-0 text-primary hover:bg-primary/10"
									>
										<Download className="w-4 h-4" />
									</Button>
								</a>
							</CardContent>
						</Card>
					</motion.div>
				)}

				{/* Modals */}
				<PensionPlanTransferJourneyV2
					isOpen={isTransferModalOpen}
					onClose={() => setIsTransferModalOpen(false)}
				/>
				<IndividualPensionPlanFlowV2
					isOpen={showExternalPlanFlow}
					// Aquí se entra desde la tarjeta de plan externo, pulsando a propósito:
					// se abre el formulario con lo declarado este año para poder corregirlo,
					// en vez de darlo por bueno y seguir.
					mode="edit"
					onClose={() => setShowExternalPlanFlow(false)}
					onComplete={() => {
						setShowExternalPlanFlow(false);
						fetchIndividualPlan();
						// El tope del slider también depende de lo declarado.
						fetchFreelanceMaxContribution();
					}}
					token={token}
				/>
				<ChangeContributionDaySheet
					open={isChangeDaySheetOpen}
					currentDay={contributionDay ?? 5}
					onClose={() => setIsChangeDaySheetOpen(false)}
					onConfirm={handleChangeContributionDay}
					translate={translate}
					langCode={langCode}
				/>
				<RescueRequestDialogV2
					userEppConfigId={individualEppConfigId}
					token={token}
					onSubmitted={() => refreshIndividualWithdrawals()}
					isOpen={showRescueDialog}
					onClose={() => setShowRescueDialog(false)}
					availableAmount={totalSaved}
					email={userEmail}
					currentReason={individualStoppingReason}
				/>
				<Dialog open={showCalculatorModal} onOpenChange={setShowCalculatorModal}>
					<DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto thin-scrollbar">
						<div className="individual-v2 -m-6 p-6 rounded-lg">
							<DialogHeader>
								<DialogTitle className="font-heading">
									{translate('rescue.individualDashboard.calcDialogTitle')}
								</DialogTitle>
							</DialogHeader>
							<div className="mt-6">
								<RescueTaxSimulatorV2
									initialAvailable={totalSaved}
									cardTitle={translate('rescue.individualDashboard.calcCardTitle')}
								/>
							</div>
						</div>
					</DialogContent>
				</Dialog>
				<IbanRequiredSheet
					open={(ibanSheetOpen || ibanKycSyncing) && !ibanSyncDismissed}
					onClose={() => {
						setIbanSheetOpen(false);
						setIbanSyncDismissed(true);
					}}
					onStart={handleStartIbanKyc}
					// También mientras se sincroniza la vuelta: el usuario acaba de
					// firmar y la pantalla le dice que se está terminando, en vez de
					// devolverle un dashboard que todavía cree que no tiene cuenta.
					loading={ibanSheetLoading || ibanKycSyncing}
					error={ibanSheetError}
					translate={translate}
				/>

				<MonthlyContributionSheet
					open={monthlyPresetDelta !== null}
					initialAmount={presetInitialAmount}
					currentAmount={savedMonthlyContribution}
					maxAmount={freelanceMaxContribution}
					onClose={() => setMonthlyPresetDelta(null)}
					onConfirm={handleConfirmMonthlyPreset}
					translate={translate}
				/>

				<OneTimeContributionSheet
					open={isOneTimeSheetOpen}
					maxAmount={oneTimeLimits?.remaining}
					minAmount={oneTimeLimits?.minAmount}
					// Guard de "una al día". `undefined` (sin dato fresco y sin haber
					// aportado en esta sesión) NO bloquea: manda el backend al confirmar.
					canContributeToday={canContributeToday}
					loadingLimits={loadingOneTimeLimits}
					onClose={() => setIsOneTimeSheetOpen(false)}
					onConfirm={handleOneTimeContribution}
					translate={translate}
				/>
			</div>
		</div>
	);
};

export default IndividualDashboardV2;
